import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MarketingLead, MarketingLeadStatus, MarketingLeadSource, Agency, MarketingLeadStatusHistory, UserProfile, DealStage, MarketingLeadNote, MarketingLeadDocument, PropertyDetails, EstimationResult, MarketingProvider, Reminder, SequenceStep, SequenceTemplate, StepType, StepStatus } from '../types';
import { storage } from '../services/storage';
import { supabase } from '../lib/supabase';
import { useModal } from './ui/ModalProvider';
import { SearchableSelect } from './ui/SearchableSelect';
import { EstimationPanelWithTabs } from './EstimationPanelWithTabs';
import { 
  Search, Filter, CheckCircle2, Clock, XCircle, UserCheck, UserX, 
  Building, MapPin, Mail, Phone, Calendar, TrendingUp, Facebook,
  Edit2, ArrowRight, RefreshCw, FileText, ChevronDown, ChevronUp, Plus, Send,
  Paperclip, MessageSquare, Upload, Download, Trash2, X, ExternalLink, Eye, Calculator,
  Tag, Newspaper, Zap, CheckSquare, EyeOff
} from 'lucide-react';

interface MarketingLeadsPanelProps {
  userProfile: any;
  initialLeadId?: string;
  initialTab?: 'notes' | 'reminders' | 'sequence' | 'documents' | 'history';
  onClearInitialLead?: () => void;
}

const STATUS_LABELS: Record<MarketingLeadStatus, { label: string; color: string; icon: any }> = {
  nouveau: { label: 'Nouveau', color: 'bg-blue-100 text-blue-800', icon: Clock },
  en_qualification: { label: 'En qualification', color: 'bg-amber-100 text-amber-800', icon: RefreshCw },
  qualifie: { label: 'Qualifié', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  transfere: { label: 'Transféré', color: 'bg-purple-100 text-purple-800', icon: UserCheck },
  rejete: { label: 'Rejeté', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const SOURCE_LABELS: Record<MarketingLeadSource, { label: string; color: string; icon: any }> = {
  meta: { label: 'Meta', color: 'bg-blue-100 text-blue-800', icon: Facebook },
  manual: { label: 'Manuel', color: 'bg-green-100 text-green-800', icon: Plus },
  call: { label: 'Appel', color: 'bg-purple-100 text-purple-800', icon: Phone },
  other: { label: 'Autre', color: 'bg-slate-100 text-slate-800', icon: FileText },
  estimation_form: { label: 'Formulaire Estimation', color: 'bg-amber-100 text-amber-800', icon: TrendingUp },
  leboncoin: { label: 'Leboncoin', color: 'bg-orange-100 text-orange-800', icon: Tag },
  pap: { label: 'PAP', color: 'bg-indigo-100 text-indigo-800', icon: Newspaper },
  seloger: { label: 'SeLoger', color: 'bg-cyan-100 text-cyan-800', icon: Building }
};

const REJECTION_REASONS = [
  'Locataire déjà trouvé',
  'Projet reporté / abandonné',
  'Bien non encore disponible (travaux, succession, occupation en cours)',
  'Location Courte Durée interdite par le règlement de copropriété',
  'Refus du syndic',
  'Zone réglementée / restrictions municipales',
  'Bien non conforme à la LCD (surface, normes, usage)',
  'Refus de la sous-location',
  'Refus du modèle conciergerie',
  'Manque de confiance / réticence générale',
  'Propriétaire injoignable',
  'Aucune réponse après 2 relances effectuées',
  'Numéro incorrect / email invalide',
  'Typologie du bien non adaptée (chambre, colocation, etc.)',
  'Mobilier insuffisant / refus d\'investissement',
  'État du bien incompatible avec l\'exploitation',
  'Hors zone géographique couverte',
  'Hors typologie recherchée',
  'Volume ou potentiel insuffisant',
  'Profil propriétaire non aligné avec notre stratégie',
  'Annonce désactivée',
  'Annonce mise en pause',
  'Autres motifs'
];

export const MarketingLeadsPanel: React.FC<MarketingLeadsPanelProps> = ({ userProfile, initialLeadId, initialTab, onClearInitialLead }) => {
  const { toast, confirm, prompt } = useModal();
  const [leads, setLeads] = useState<MarketingLead[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<MarketingLead | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isRejectionReasonModalOpen, setIsRejectionReasonModalOpen] = useState(false);
  const [selectedLeadForRejection, setSelectedLeadForRejection] = useState<string | null>(null);
  const [selectedRejectionReason, setSelectedRejectionReason] = useState<string>('');
  const [statusHistory, setStatusHistory] = useState<MarketingLeadStatusHistory[]>([]);
  const [assignmentHistory, setAssignmentHistory] = useState<Array<{
    id: string;
    leadId: string;
    assignedToUserId: string | null;
    assignedByUserId: string | null;
    assignmentMethod: 'auto' | 'manual' | 'reassigned';
    reason: string | null;
    previousAssignedToUserId: string | null;
    createdAt: string;
  }>>([]);

  // Filtres
  const [statusFilter, setStatusFilter] = useState<MarketingLeadStatus | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState<MarketingLeadSource | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [campaignFilter, setCampaignFilter] = useState<string>('all');
  const [hideToRelaunch, setHideToRelaunch] = useState(true); // Actif par défaut
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());

  // Drag & Drop State
  const [draggingLeadId, setDraggingLeadId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);

  // Stats rapides
  const [stats, setStats] = useState({
    total: 0,
    byStatus: {} as Record<MarketingLeadStatus, number>,
    bySource: {} as Record<MarketingLeadSource, number>
  });

  useEffect(() => {
    loadData();
    loadAgencies();
    loadProfiles();
  }, []);

  // Gérer l'ouverture initiale de la modale depuis la navigation (dashboard → tâche → lead)
  useEffect(() => {
    if (!initialLeadId || loading) return;
    const leadInList = leads.find(l => l.id === initialLeadId);
    if (leadInList) {
      setSelectedLead(leadInList);
      setIsDetailModalOpen(true);
      onClearInitialLead?.();
      return;
    }
    // Lead non présent dans la liste chargée (ex. au-delà de la limite) : le charger par ID
    let cancelled = false;
    storage.getMarketingLead(initialLeadId).then((fetched) => {
      if (cancelled || !fetched) return;
      setLeads(prev => (prev.some(l => l.id === fetched.id) ? prev : [fetched, ...prev]));
      setSelectedLead(fetched);
      setIsDetailModalOpen(true);
      onClearInitialLead?.();
    });
    return () => { cancelled = true; };
  }, [initialLeadId, loading, leads, onClearInitialLead]);

  const loadProfiles = async () => {
    const profilesList = await storage.getAllProfiles();
    console.log('Loaded profiles:', {
      total: profilesList.length,
      marketing: profilesList.filter(p => p.role === 'prestataire_marketing').length,
      marketingDetails: profilesList.filter(p => p.role === 'prestataire_marketing').map(p => ({
        id: p.id,
        name: `${p.firstName} ${p.lastName}`,
        email: p.email,
        providerId: p.marketingProviderId,
        isSupervisor: p.isSupervisor
      }))
    });
    setProfiles(profilesList);
  };

  const getAssignedByUserName = (userId?: string | null): string => {
    if (!userId) return '—';
    const profile = profiles.find(p => p.id === userId);
    if (!profile) return '—';
    return profile.firstName && profile.lastName
      ? `${profile.firstName} ${profile.lastName}`
      : profile.email || '—';
  };

  useEffect(() => {
    loadData();
  }, [statusFilter, sourceFilter, campaignFilter]);

  const loadAgencies = async () => {
    const agenciesList = await storage.getAllAgencies();
    setAgencies(agenciesList.filter(a => a.id)); // Filtrer les agences actives
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }
      if (sourceFilter !== 'all') {
        filters.source = sourceFilter;
      }
      if (campaignFilter !== 'all') {
        filters.campaignId = campaignFilter;
      }
      if (searchQuery) {
        filters.search = searchQuery;
      }
      filters.limit = 2000; // Charger suffisamment de leads pour le pipeline et la navigation depuis le dashboard

      const leadsData = await storage.getMarketingLeads(filters);
      setLeads(leadsData);

      // Calculer les stats (même limite pour cohérence)
      const allLeads = await storage.getMarketingLeads({ limit: 2000 });
      const statusCounts: Record<MarketingLeadStatus, number> = {
        nouveau: 0,
        en_qualification: 0,
        qualifie: 0,
        transfere: 0,
        rejete: 0
      };
      const sourceCounts: Record<MarketingLeadSource, number> = {
        meta: 0,
        manual: 0,
        call: 0,
        other: 0,
        estimation_form: 0,
        leboncoin: 0,
        pap: 0
      };
      allLeads.forEach(lead => {
        statusCounts[lead.status]++;
        sourceCounts[lead.source]++;
      });
      setStats({
        total: allLeads.length,
        byStatus: statusCounts,
        bySource: sourceCounts
      });
    } catch (error: any) {
      toast.error('Erreur lors du chargement des leads: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId: string, newStatus: MarketingLeadStatus, comment?: string) => {
    // Validation selon le statut
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    if (newStatus === 'qualifie') {
      // Vérifier les champs obligatoires
      if (!lead.email && !lead.phone) {
        toast.error('Un email ou un téléphone est requis pour qualifier un lead');
        return;
      }
      if (!lead.propertyAddress) {
        toast.error('L\'adresse du bien est requise pour qualifier un lead');
        return;
      }
    }

    if (newStatus === 'rejete') {
      // Ouvrir le modal de sélection de raison de rejet
      setSelectedLeadForRejection(leadId);
      setSelectedRejectionReason('');
      setIsRejectionReasonModalOpen(true);
      return; // On ne continue pas, le rejet sera confirmé via le modal
    }

    const { data, error } = await storage.updateMarketingLeadStatus(leadId, newStatus, comment);
    if (error) {
      toast.error('Erreur lors du changement de statut: ' + (error.message || 'Erreur inconnue'));
    } else {
      toast.success('Statut mis à jour avec succès');
      loadData();
      if (selectedLead?.id === leadId) {
        setSelectedLead(data);
        loadStatusHistory(leadId);
      }
    }
  };

  const handleConfirmRejection = async () => {
    if (!selectedLeadForRejection) return;
    
    if (!selectedRejectionReason || selectedRejectionReason.trim() === '') {
      toast.error('Veuillez sélectionner un motif de rejet');
      return;
    }

    const reason = selectedRejectionReason.trim();
    
    // Mettre à jour le lead avec le motif de rejet
    await storage.updateMarketingLead(selectedLeadForRejection, { rejectionReason: reason });
    
    // Mettre à jour le statut avec le commentaire
    const { data, error } = await storage.updateMarketingLeadStatus(selectedLeadForRejection, 'rejete', reason);
    
    if (error) {
      toast.error('Erreur lors du rejet: ' + (error.message || 'Erreur inconnue'));
    } else {
      toast.success('Lead rejeté avec succès');
      loadData();
      if (selectedLead?.id === selectedLeadForRejection) {
        setSelectedLead(data);
        loadStatusHistory(selectedLeadForRejection);
      }
      // Fermer le modal
      setIsRejectionReasonModalOpen(false);
      setSelectedLeadForRejection(null);
      setSelectedRejectionReason('');
    }
  };

  const handleTransferToAgency = async (leadId: string, agencyId: string, pipelineStage: DealStage, hasAppointment: boolean, appointmentDate?: string, appointmentNotes?: string) => {
    const { data, error, dealId } = await storage.transferMarketingLeadToAgency(
      leadId,
      agencyId,
      pipelineStage,
      hasAppointment,
      appointmentDate,
      appointmentNotes
    );
    if (error) {
      toast.error('Erreur lors du transfert: ' + (error.message || 'Erreur inconnue'));
    } else {
      toast.success(`Lead transféré avec succès ! Deal créé (ID: ${dealId})`);
      loadData();
      setIsDetailModalOpen(false);
      setIsTransferModalOpen(false);
    }
  };

  const handleCreateManualLead = async (leadData: Partial<MarketingLead>) => {
    // Vérifier que le providerId est présent
    if (!leadData.providerId) {
      toast.error('Erreur: Le prestataire marketing est requis pour créer un lead');
      return;
    }

    const newLead: Partial<MarketingLead> = {
      ...leadData,
      source: leadData.source || 'other',
      status: 'nouveau' as MarketingLeadStatus
    };
    const { data, error } = await storage.saveMarketingLead(newLead);
    if (error) {
      toast.error('Erreur lors de la création: ' + (error.message || 'Erreur inconnue'));
    } else {
      // Créer automatiquement une note avec l'URL si elle est remplie
      if (data && data.id && leadData.annonceUrl && leadData.annonceUrl.trim()) {
        await storage.addMarketingLeadNote(data.id, `URL de l'annonce : ${leadData.annonceUrl.trim()}`);
      }
      toast.success('Lead créé avec succès');
      loadData();
      setIsCreateModalOpen(false);
    }
  };

  const handleUpdateLead = async (leadId: string, updates: Partial<MarketingLead>) => {
    const { data, error } = await storage.updateMarketingLead(leadId, updates);
    if (error) {
      toast.error('Erreur lors de la mise à jour: ' + (error.message || 'Erreur inconnue'));
    } else {
      toast.success('Lead mis à jour avec succès');
      loadData();
      if (selectedLead?.id === leadId) {
        setSelectedLead(data);
      }
    }
  };

  const openDetailModal = async (lead: MarketingLead) => {
    // Recharger le lead complet avec notes et documents
    const fullLead = await storage.getMarketingLead(lead.id);
    if (fullLead) {
      setSelectedLead(fullLead);
      setIsDetailModalOpen(true);
      await loadStatusHistory(lead.id);
    } else {
      setSelectedLead(lead);
      setIsDetailModalOpen(true);
      await loadStatusHistory(lead.id);
    }
  };

  const openTransferModal = async (lead: MarketingLead) => {
    setSelectedLead(lead);
    setIsTransferModalOpen(true);
  };

  const loadStatusHistory = async (leadId: string) => {
    const [history, assignments] = await Promise.all([
      storage.getMarketingLeadStatusHistory(leadId),
      storage.getMarketingLeadAssignmentHistory(leadId)
    ]);
    setStatusHistory(history);
    setAssignmentHistory(assignments);
  };

  const toggleLeadSelection = (leadId: string) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
  };

  const handleBulkAssign = async (agencyId: string) => {
    if (!agencyId || selectedLeads.size === 0) return;
    
    const confirmed = await confirm({
      title: 'Affecter les leads sélectionnés ?',
      message: `${selectedLeads.size} lead(s) seront affectés à cette agence.`,
      confirmText: 'Confirmer',
      cancelText: 'Annuler'
    });
    
    if (!confirmed) return;
    
    // Pour chaque lead sélectionné, on le transfère
    // Note: Cette fonctionnalité nécessiterait une logique plus complexe
    // Pour l'instant, on affiche juste un message
    toast.success(`${selectedLeads.size} lead(s) sélectionné(s) - Fonctionnalité à implémenter`);
    setSelectedLeads(new Set());
  };

  // Liste des campagnes uniques
  const campaigns = Array.from(new Set(leads.map(l => l.campaignName).filter(Boolean)));

  // Filtrer les leads selon la recherche et les filtres
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      // Filtre recherche
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = (
          (lead.firstName?.toLowerCase().includes(query)) ||
          (lead.lastName?.toLowerCase().includes(query)) ||
          (lead.email?.toLowerCase().includes(query)) ||
          (lead.phone?.includes(query)) ||
          (lead.propertyAddress?.toLowerCase().includes(query)) ||
          (lead.campaignName?.toLowerCase().includes(query))
        );
        if (!matchesSearch) return false;
      }

      // Filtre statut (si 'all', on montre tout)
      if (statusFilter !== 'all' && lead.status !== statusFilter) return false;

      // Filtre source
      if (sourceFilter !== 'all' && lead.source !== sourceFilter) return false;

      // Filtre campagne
      if (campaignFilter !== 'all' && lead.campaignName !== campaignFilter) return false;

      // Filtre "Masquer les à relancer" : exclure les leads avec un reminder ayant hideUntilDate: true et une date future
      if (hideToRelaunch) {
        const hasActiveHideReminder = lead.reminders?.some(r => 
          r.hideUntilDate && 
          !r.isDone && 
          new Date(r.date) > new Date()
        );
        if (hasActiveHideReminder) {
          return false; // Exclure ce lead
        }
      }

      return true;
    });
  }, [leads, searchQuery, statusFilter, sourceFilter, campaignFilter, hideToRelaunch]);

  // Drag & Drop Handlers
  const handleDragStart = (e: React.DragEvent, leadId: string) => {
    e.dataTransfer.setData("leadId", leadId);
    e.dataTransfer.effectAllowed = "move";
    setDraggingLeadId(leadId);
  };

  const handleDragOver = (e: React.DragEvent, status: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStatus !== status) setDragOverStatus(status);
  };

  const handleDrop = async (e: React.DragEvent, newStatus: MarketingLeadStatus) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("leadId");
    if (leadId) {
      const lead = leads.find(l => l.id === leadId);
      if (lead && lead.status !== newStatus) {
        // Si on passe de "qualifie" à "transfere", ouvrir la modale de transfert
        if (lead.status === 'qualifie' && newStatus === 'transfere') {
          setSelectedLead(lead);
          setIsTransferModalOpen(true);
        } else {
          await handleStatusChange(leadId, newStatus);
        }
      }
    }
    setDraggingLeadId(null);
    setDragOverStatus(null);
  };

  const handleDragEnd = () => {
    setDraggingLeadId(null);
    setDragOverStatus(null);
  };

  // Récupérer les agences accessibles pour l'utilisateur (setter)
  const getUserAgencies = async () => {
    const session = await supabase.auth.getSession();
    const userId = session.data.session?.user?.id;
    if (!userId) return agencies;
    
    const userAgencies = await storage.getMarketingProviderUserAgencies(userId);
    if (userAgencies.length === 0) return agencies; // Superviseur voit toutes les agences
    return agencies.filter(a => userAgencies.includes(a.id));
  };

  const [accessibleAgencies, setAccessibleAgencies] = useState<Agency[]>(agencies);

  useEffect(() => {
    getUserAgencies().then(setAccessibleAgencies);
  }, [agencies]);

  return (
    <div className="p-8 flex flex-col h-screen">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center mb-2">
            <TrendingUp className="w-8 h-8 text-amber-600 mr-3" />
            <h1 className="text-2xl font-bold text-slate-900">Gestion des Leads</h1>
          </div>
          <p className="text-slate-500">Gérez et qualifiez vos leads (Meta, manuels, appels...)</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 font-medium shadow-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          Créer un lead manuel
        </button>
      </div>

      {/* Stats rapides - Style Pipeline */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-2 opacity-10"><TrendingUp className="w-12 h-12 text-slate-500" /></div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Total</p>
          <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
        </div>
        {Object.entries(STATUS_LABELS).map(([status, config]) => {
          const Icon = config.icon;
          const count = stats.byStatus[status as MarketingLeadStatus] || 0;
          const isTransfere = status === 'transfere';
          const isRejete = status === 'rejete';
          return (
            <div key={status} className={`rounded-xl shadow-sm border p-4 relative overflow-hidden ${
              isTransfere ? 'bg-purple-50 border-purple-100' :
              isRejete ? 'bg-red-50 border-red-100' :
              status === 'qualifie' ? 'bg-green-50 border-green-100' :
              status === 'en_qualification' ? 'bg-amber-50 border-amber-100' :
              'bg-blue-50 border-blue-100'
            }`}>
              <div className={`absolute top-0 right-0 p-2 opacity-10`}>
                <Icon className={`w-12 h-12 ${
                  isTransfere ? 'text-purple-500' :
                  isRejete ? 'text-red-500' :
                  status === 'qualifie' ? 'text-green-500' :
                  status === 'en_qualification' ? 'text-amber-500' :
                  'text-blue-500'
                }`} />
              </div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{config.label}</p>
              <div className={`text-2xl font-bold ${
                isTransfere ? 'text-purple-600' :
                isRejete ? 'text-red-600' :
                status === 'qualifie' ? 'text-green-600' :
                status === 'en_qualification' ? 'text-amber-600' :
                'text-blue-600'
              }`}>
                {count}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none z-10" />
            <input
              type="text"
              placeholder="Rechercher un lead..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white text-slate-900 transition-all"
            />
          </div>
          <SearchableSelect
            options={[
              { value: 'all', label: 'Tous les statuts' },
              ...Object.entries(STATUS_LABELS).map(([status, config]) => ({
                value: status,
                label: config.label,
                icon: React.createElement(config.icon, { className: 'w-4 h-4' })
              }))
            ]}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as MarketingLeadStatus | 'all')}
            placeholder="Statut"
            icon={<Filter className="w-4 h-4" />}
            searchPlaceholder="Rechercher un statut..."
            showAllOnOpen={true}
          />
          <SearchableSelect
            options={[
              { value: 'all', label: 'Toutes les sources' },
              ...Object.entries(SOURCE_LABELS).map(([source, config]) => ({
                value: source,
                label: config.label,
                icon: React.createElement(config.icon, { className: 'w-4 h-4' })
              }))
            ]}
            value={sourceFilter}
            onChange={(value) => setSourceFilter(value as MarketingLeadSource | 'all')}
            placeholder="Source"
            icon={<TrendingUp className="w-4 h-4" />}
            searchPlaceholder="Rechercher une source..."
            showAllOnOpen={true}
          />
          <SearchableSelect
            options={[
              { value: 'all', label: 'Toutes les campagnes' },
              ...campaigns.map(campaign => ({
                value: campaign,
                label: campaign
              }))
            ]}
            value={campaignFilter}
            onChange={(value) => setCampaignFilter(value)}
            placeholder="Campagne"
            icon={<Facebook className="w-4 h-4" />}
            searchPlaceholder="Rechercher une campagne..."
            showAllOnOpen={true}
          />
          <button
            onClick={() => setHideToRelaunch(!hideToRelaunch)}
            className={`px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors shadow-sm border ${
              hideToRelaunch 
                ? 'bg-slate-800 text-white border-slate-800 hover:bg-slate-700' 
                : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <EyeOff className="w-4 h-4" />
            {hideToRelaunch ? 'Afficher les à relancer' : 'Masquer les à relancer'}
          </button>
          <button
            onClick={loadData}
            className="px-4 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center gap-2 font-medium transition-colors shadow-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Vue Kanban */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          <p className="text-slate-500 mt-4">Chargement...</p>
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Aucun lead trouvé</p>
          <p className="text-sm text-slate-400 mt-1">Aucun lead ne correspond aux critères sélectionnés</p>
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto overflow-y-auto pb-4 bg-gray-50 rounded-xl min-h-0">
          <div className="flex space-x-4 min-w-max p-2 h-full">
            {Object.entries(STATUS_LABELS).map(([status, config]) => {
              const StatusIcon = config.icon;
              const statusLeads = filteredLeads.filter((l) => l.status === status);
              
              // Visual styles for archived columns
              const isTransfereColumn = status === 'transfere';
              const isRejeteColumn = status === 'rejete';
              let headerColor = "bg-slate-100 border-slate-200";
              if (isTransfereColumn) headerColor = "bg-purple-50 border-purple-200";
              if (isRejeteColumn) headerColor = "bg-red-50 border-red-200";

              // Drag Over Visual
              const isDragOver = dragOverStatus === status;

                return (
                <div 
                  key={status} 
                  onDragOver={(e) => handleDragOver(e, status)}
                  onDrop={(e) => handleDrop(e, status as MarketingLeadStatus)}
                  className={`w-80 flex-shrink-0 flex flex-col rounded-xl h-full border transition-colors duration-200 
                    ${isTransfereColumn || isRejeteColumn ? 'bg-white' : 'bg-slate-100/80'} 
                    ${isDragOver ? 'ring-2 ring-amber-400 bg-amber-50 border-amber-300' : headerColor}
                  `}
                >
                  <div className={`p-3 border-b rounded-t-xl ${headerColor}`}>
                    <div className="flex justify-between items-center mb-1">
                      <h3 className={`font-semibold text-sm uppercase flex items-center ${
                        isTransfereColumn ? 'text-purple-800' : 
                        isRejeteColumn ? 'text-red-800' : 
                        status === 'qualifie' ? 'text-green-800' :
                        status === 'en_qualification' ? 'text-amber-800' :
                        'text-blue-800'
                      }`}>
                        <StatusIcon className="w-4 h-4 mr-1"/>
                        {config.label}
                        <span className="ml-2 bg-white/50 text-slate-900 text-[10px] px-2 py-0.5 rounded-full font-bold border border-slate-200/50">
                          {statusLeads.length}
                        </span>
                      </h3>
                      </div>
                      </div>

                  <div className="p-2 flex-1 overflow-y-auto space-y-2 custom-scrollbar">
                    {statusLeads.map((lead) => {
                      const now = new Date();
                      now.setHours(0,0,0,0);
                      const receivedDate = new Date(lead.receivedAt);
                      receivedDate.setHours(0,0,0,0);
                      const daysSinceReceived = Math.floor((now.getTime() - receivedDate.getTime()) / (1000 * 60 * 60 * 24));
                      const isLate = daysSinceReceived > 3 && lead.status === 'nouveau';
                      const isDragging = draggingLeadId === lead.id;
                      const SourceIcon = SOURCE_LABELS[lead.source]?.icon || SOURCE_LABELS.other.icon;

                      return (
                        <div 
                          key={lead.id}
                          draggable={!isTransfereColumn && !isRejeteColumn}
                          onDragStart={(e) => lead.id && handleDragStart(e, lead.id)}
                          onDragEnd={handleDragEnd}
                          onClick={() => openDetailModal(lead)}
                          className={`bg-white p-3 rounded-lg shadow-sm border border-slate-200 group relative hover:shadow-md transition-all cursor-pointer 
                            ${isLate ? 'ring-2 ring-red-100 border-red-200' : ''}
                            ${isDragging ? 'opacity-50 scale-95 rotate-1 shadow-xl ring-2 ring-amber-400' : ''}
                            ${(!isTransfereColumn && !isRejeteColumn) ? 'hover:-translate-y-1' : ''}
                          `}
                        >
                          <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${
                            lead.source === 'meta' ? 'bg-blue-500' :
                            lead.source === 'estimation_form' ? 'bg-amber-500' :
                            lead.source === 'call' ? 'bg-purple-500' :
                            lead.source === 'leboncoin' ? 'bg-orange-500' :
                            lead.source === 'pap' ? 'bg-indigo-500' :
                            'bg-green-500'
                          }`}></div>
                          <div className="pl-2">
                            <div className="flex justify-between items-start mb-1">
                              <div className="flex items-center space-x-1 flex-wrap gap-y-1">
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border flex items-center uppercase tracking-tighter ${SOURCE_LABELS[lead.source]?.color || SOURCE_LABELS.other.color}`}>
                                  <SourceIcon className="w-2.5 h-2.5 mr-1" />
                                  {SOURCE_LABELS[lead.source]?.label || lead.source || SOURCE_LABELS.other.label}
                                </span>
                                {lead.campaignName && (
                                  <span className="text-[10px] text-slate-500 bg-slate-50 border border-slate-100 px-1.5 rounded flex items-center truncate max-w-[120px]" title={lead.campaignName}>
                                    {lead.campaignName}
                                  </span>
                                )}
                      </div>
                            </div>
                            <h4 className="font-semibold text-slate-800 text-sm truncate leading-tight mt-2">
                              {lead.firstName} {lead.lastName}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                        {lead.email && (
                                <span className="flex items-center gap-1 truncate">
                                  <Mail className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate max-w-[140px]">{lead.email}</span>
                          </span>
                        )}
                        {lead.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3 flex-shrink-0" />
                            {lead.phone}
                          </span>
                        )}
                      </div>
                            {lead.propertyAddress && (
                              <p className="text-xs text-slate-500 mb-2 truncate flex items-center gap-1">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                {lead.propertyAddress}
                                {lead.propertyCity && `, ${lead.propertyCity}`}
                              </p>
                            )}
                            {lead.assignedToUserId ? (
                              <div className="flex items-center gap-1.5 mb-2">
                                <UserCheck className="w-3 h-3 text-amber-600 flex-shrink-0" />
                                <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 px-1.5 rounded">
                                  {getAssignedByUserName(lead.assignedToUserId)}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 mb-2">
                                <UserX className="w-3 h-3 text-red-600 flex-shrink-0" />
                                <span className="text-[10px] text-red-700 bg-red-50 border border-red-100 px-1.5 rounded font-medium">
                                  Non affecté
                                </span>
                              </div>
                            )}
                            {(() => {
                              // Trouver le prochain reminder non fait
                              const nextReminder = (lead.reminders || [])
                                .filter(r => !r.isDone)
                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
                              
                              // Trouver le prochain sequence step PENDING
                              const nextSequenceStep = (lead.sequenceSteps || [])
                                .filter(s => s.status === 'PENDING')
                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
                              
                              // Déterminer quelle tâche afficher (priorité au reminder)
                              const nextTask = nextReminder || nextSequenceStep;
                              
                              if (nextTask && !isTransfereColumn && !isRejeteColumn) {
                                const taskDate = new Date(nextTask.date);
                                taskDate.setHours(0, 0, 0, 0);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const isTaskLate = taskDate < today;
                                const isTaskToday = taskDate.getTime() === today.getTime();
                                
                                const isReminder = !!nextReminder;
                                const taskType = isReminder ? 'REMINDER' : 'SEQUENCE';
                                
                                let icon = null;
                                if (isReminder) {
                                  icon = <Clock className="w-3 h-3" />;
                                } else {
                                  const step = nextSequenceStep;
                                  if (step.type === 'EMAIL') icon = <Mail className="w-3 h-3" />;
                                  else if (step.type === 'CALL') icon = <Phone className="w-3 h-3" />;
                                  else if (step.type === 'WHATSAPP') icon = <MessageCircle className="w-3 h-3" />;
                                  else if (step.type === 'SMS') icon = <MessageSquare className="w-3 h-3" />;
                                  else icon = <Zap className="w-3 h-3" />;
                                }
                                
                                return (
                                  <div className={`mt-2 p-1.5 rounded text-xs flex items-center ${
                                    isTaskLate ? 'bg-red-50 text-red-700' : 
                                    isTaskToday ? 'bg-amber-50 text-amber-700' : 
                                    'bg-blue-50 text-blue-700'
                                  }`}>
                                    {icon}
                                    <span className="truncate font-medium ml-1.5">
                                      {isTaskLate ? 'En retard : ' : isTaskToday ? 'Aujourd\'hui : ' : ''}
                                      {new Date(nextTask.date).toLocaleDateString()} - {isReminder ? nextReminder.note : nextSequenceStep.title}
                                    </span>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            {isLate && !isTransfereColumn && !isRejeteColumn && (
                              <div className="mt-2 p-1.5 rounded text-xs flex items-center bg-red-50 text-red-700">
                                <Clock className="w-3 h-3 mr-1.5 flex-shrink-0" />
                                <span className="truncate font-medium">
                                  Reçu il y a {daysSinceReceived} jour{daysSinceReceived > 1 ? 's' : ''}
                        </span>
                              </div>
                            )}
                            <div className="flex items-center justify-between text-[10px] text-slate-400 pt-2 border-t border-slate-50 mt-2">
                              <div className="flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                {new Date(lead.receivedAt).toLocaleDateString()}
                            </div>
                              </div>
                              </div>
                          </div>
                      );
                    })}
                    {!isTransfereColumn && !isRejeteColumn && (
                      <button
                        onClick={() => setIsCreateModalOpen(true)} 
                        className="w-full py-2 text-sm text-slate-500 hover:bg-slate-200/50 rounded-lg border border-dashed border-slate-300 flex items-center justify-center transition-colors"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Ajouter
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Modal de détail */}
      {isDetailModalOpen && selectedLead && (
        <MarketingLeadDetailModal
          key={`${selectedLead.id}-${initialTab || 'notes'}`}
          lead={selectedLead}
          agencies={accessibleAgencies}
          profiles={profiles}
          statusHistory={statusHistory}
          assignmentHistory={assignmentHistory}
          userProfile={userProfile}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedLead(null);
            // Recharger les données pour mettre à jour la vue Kanban
            loadData();
          }}
          onStatusChange={handleStatusChange}
          onTransfer={openTransferModal}
          onUpdate={handleUpdateLead}
          onReload={loadData}
          initialTab={initialTab}
        />
      )}

      {/* Modal de création manuelle */}
      {isCreateModalOpen && (
        <CreateManualLeadModal
          userProfile={userProfile}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateManualLead}
        />
      )}

      {/* Modal de transfert */}
      {isTransferModalOpen && selectedLead && (
        <TransferLeadModal
          lead={selectedLead}
          agencies={accessibleAgencies}
          onClose={() => {
            setIsTransferModalOpen(false);
            setSelectedLead(null);
          }}
          onTransfer={handleTransferToAgency}
        />
      )}

      {/* Modal de sélection de raison de rejet */}
      {isRejectionReasonModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Motif de rejet
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Veuillez sélectionner le motif de rejet (obligatoire)
              </p>
              
              <div className="mb-4">
                <SearchableSelect
                  options={REJECTION_REASONS.map((reason) => ({
                    value: reason,
                    label: reason
                  }))}
                  value={selectedRejectionReason}
                  onChange={(value) => setSelectedRejectionReason(value)}
                  placeholder="Rechercher ou sélectionner un motif..."
                  searchPlaceholder="Tapez pour rechercher..."
                  showAllOnOpen={true}
                  allowClear={true}
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsRejectionReasonModalOpen(false);
                    setSelectedLeadForRejection(null);
                    setSelectedRejectionReason('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmRejection}
                  disabled={!selectedRejectionReason || selectedRejectionReason.trim() === ''}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Confirmer le rejet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Section Notes
interface NotesSectionProps {
  lead: MarketingLead;
  onReload: () => void;
  userProfile?: UserProfile | null;
}

const NotesSection: React.FC<NotesSectionProps> = ({ lead, onReload, userProfile }) => {
  const { toast, confirm } = useModal();
  const [newNote, setNewNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [notes, setNotes] = useState<MarketingLeadNote[]>(lead.notes || []);

  useEffect(() => {
    setNotes(lead.notes || []);
  }, [lead.notes]);

  const displayAuthorName = (noteAuthorName?: string): string => {
    if (!noteAuthorName || !userProfile) return noteAuthorName || '—';
    const currentUserName = userProfile.firstName && userProfile.lastName
      ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
      : userProfile.email?.split('@')[0] || '';
    return noteAuthorName === currentUserName ? 'Moi' : noteAuthorName;
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Veuillez saisir une note');
      return;
    }

    setIsAddingNote(true);
    try {
      const { data, error } = await storage.addMarketingLeadNote(lead.id, newNote.trim());
      if (error) {
        toast.error('Erreur lors de l\'ajout de la note: ' + (error.message || 'Erreur inconnue'));
      } else {
        toast.success('Note ajoutée avec succès');
        setNewNote('');
        onReload();
        if (data) {
          setNotes([...notes, data]);
        }
      }
    } catch (error: any) {
      toast.error('Erreur: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    const confirmed = await confirm({
      title: 'Supprimer cette note ?',
      message: 'Cette action est irréversible.',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      danger: true
    });
    
    if (!confirmed) return;

    try {
      const { error } = await storage.deleteMarketingLeadNote(lead.id, noteId);
      if (error) {
        toast.error('Erreur lors de la suppression: ' + (error.message || 'Erreur inconnue'));
      } else {
        toast.success('Note supprimée');
        setNotes(notes.filter(n => n.id !== noteId));
        onReload();
      }
    } catch (error: any) {
      toast.error('Erreur: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const sortedNotes = [...notes].sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <textarea 
          className="w-full resize-none outline-none text-sm min-h-[80px] bg-transparent text-slate-900 placeholder-slate-400" 
          placeholder="Écrivez une note, un compte-rendu d'appel..." 
          value={newNote} 
          onChange={e => setNewNote(e.target.value)} 
        />
        <div className="flex justify-end mt-2 pt-2 border-t border-slate-100">
          <button 
            onClick={handleAddNote} 
            disabled={isAddingNote || !newNote.trim()} 
            className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center disabled:opacity-50 hover:bg-slate-800 transition-colors"
          >
            <Send className="w-3.5 h-3.5 mr-2" /> Envoyer
          </button>
        </div>
      </div>
      
      <div className="space-y-4 relative pl-6 border-l-2 border-slate-200 ml-4">
        {sortedNotes.map((note) => (
          <div key={note.id} className="relative group">
            <div className="absolute -left-[31px] top-4 w-4 h-4 rounded-full bg-white border-2 border-slate-300 group-hover:border-amber-500 transition-colors"></div>
            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 text-sm">
              <div className="flex justify-between items-start mb-3">
                <span className="font-bold text-slate-900">{displayAuthorName(note.authorName)}</span>
                <div className="flex items-center gap-2">
                  {(userProfile?.role === 'admin' || userProfile?.role === 'superadmin') && (
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className="text-slate-300 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <span className="text-xs text-slate-400 bg-slate-50 px-2 py-1 rounded-full">{new Date(note.createdAt).toLocaleString()}</span>
                </div>
              </div>
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{note.content}</p>
            </div>
          </div>
        ))}
        {sortedNotes.length === 0 && (
          <p className="text-slate-400 italic pl-4">Aucune note pour le moment.</p>
        )}
      </div>
    </div>
  );
};

// Section Documents
interface DocumentsSectionProps {
  lead: MarketingLead;
  onReload: () => void;
}

const DocumentsSection: React.FC<DocumentsSectionProps> = ({ lead, onReload }) => {
  const { toast, confirm } = useModal();
  const [isUploading, setIsUploading] = useState(false);
  const [documents, setDocuments] = useState<MarketingLeadDocument[]>(lead.documents || []);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDocuments(lead.documents || []);
  }, [lead.documents]);

  const handleFileUpload = async (file: File) => {
    // Vérifier la taille (max 30MB)
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 30MB)');
      return;
    }

    setIsUploading(true);
    try {
      const { data, error } = await storage.uploadMarketingLeadDocument(lead.id, file);
      if (error) {
        toast.error('Erreur lors de l\'upload: ' + (error.message || 'Erreur inconnue'));
      } else {
        toast.success('Fichier uploadé avec succès');
        if (data) {
          setDocuments([...documents, data]);
        }
        onReload();
      }
    } catch (error: any) {
      toast.error('Erreur: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleFileUpload(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    const confirmed = await confirm({
      title: 'Supprimer le fichier ?',
      message: 'Cette action est irréversible.',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      danger: true
    });

    if (!confirmed) return;

    try {
      const { error } = await storage.deleteMarketingLeadDocument(documentId);
      if (error) {
        toast.error('Erreur lors de la suppression: ' + (error.message || 'Erreur inconnue'));
      } else {
        toast.success('Fichier supprimé');
        setDocuments(documents.filter(d => d.id !== documentId));
        onReload();
      }
    } catch (error: any) {
      toast.error('Erreur: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleViewDocument = async (document: MarketingLeadDocument) => {
    try {
      const url = await storage.getMarketingLeadDocumentUrl(document.path);
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Impossible de récupérer le fichier');
      }
    } catch (error: any) {
      toast.error('Erreur: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return <FileText className="w-5 h-5 text-red-600" />;
    if (type.includes('image')) return <FileText className="w-5 h-5 text-blue-600" />;
    if (type.includes('video') || type === 'mp4') return <FileText className="w-5 h-5 text-purple-600" />;
    return <FileText className="w-5 h-5 text-slate-600" />;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-slate-900 flex items-center gap-2">
          <Paperclip className="w-5 h-5 text-amber-600" />
          Fichiers joints
        </h3>
        <span className="text-sm text-slate-500">{documents.length} fichier{documents.length > 1 ? 's' : ''}</span>
      </div>

      {/* Zone de glisser-déposer */}
      <div className="mb-4">
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors relative ${
            isDragging ? 'border-amber-500 bg-amber-50' : 'border-slate-300'
          }`}
        >
          {isUploading ? (
            <div className="flex flex-col items-center justify-center py-2">
              <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mb-3" />
              <span className="text-sm text-slate-500 font-medium">Envoi en cours...</span>
            </div>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                accept="image/*,application/pdf,.doc,.docx,audio/mp4,audio/x-m4a,.m4a,video/mp4,.mp4"
                disabled={isUploading}
              />
              <div className="bg-white w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 shadow-sm">
                <Upload className="w-5 h-5 text-amber-500" />
              </div>
              <h4 className="font-bold text-slate-800 text-sm">Ajouter un document</h4>
              <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, M4A, MP4 (Max 30Mo)</p>
            </>
          )}
        </div>
      </div>

      {/* Liste des documents */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {documents.length > 0 ? (
          documents.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-lg hover:shadow-sm transition-shadow group">
              <div className="flex items-center overflow-hidden">
                <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center text-slate-500 mr-3 shrink-0">
                  {getFileIcon(doc.type)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{doc.name}</p>
                  <div className="flex items-center mt-0.5 space-x-2">
                    <span className="text-[10px] text-slate-400">{formatFileSize(doc.size)}</span>
                    <span className="text-[10px] text-slate-400">•</span>
                    <span className="text-[10px] text-slate-400">{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString('fr-FR') : ''}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleViewDocument(doc)}
                  className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded"
                  title="Voir / Ouvrir"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteDocument(doc.id)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="text-sm text-slate-400 italic text-center py-4">Aucun document pour le moment.</p>
        )}
      </div>
    </div>
  );
};

// Modal de détail du lead
interface MarketingLeadDetailModalProps {
  lead: MarketingLead;
  agencies: Agency[];
  profiles: UserProfile[];
  statusHistory: MarketingLeadStatusHistory[];
  assignmentHistory: Array<{
    id: string;
    leadId: string;
    assignedToUserId: string | null;
    assignedByUserId: string | null;
    assignmentMethod: 'auto' | 'manual' | 'reassigned';
    reason: string | null;
    previousAssignedToUserId: string | null;
    createdAt: string;
  }>;
  userProfile: UserProfile | null;
  onClose: () => void;
  onStatusChange: (leadId: string, status: MarketingLeadStatus, comment?: string) => void;
  onTransfer: (lead: MarketingLead) => void;
  onUpdate: (leadId: string, updates: Partial<MarketingLead>) => void;
  onReload: () => void;
  initialTab?: 'notes' | 'reminders' | 'sequence' | 'documents' | 'history';
}

const getAssignedByUserName = (userId: string | null | undefined, profiles: UserProfile[]): string => {
  if (!userId) return '—';
  const profile = profiles.find(p => p.id === userId);
  if (!profile) return '—';
  return profile.firstName && profile.lastName
    ? `${profile.firstName} ${profile.lastName}`
    : profile.email || '—';
};

const MarketingLeadDetailModal: React.FC<MarketingLeadDetailModalProps> = ({
  lead: leadProp,
  agencies,
  profiles,
  statusHistory,
  assignmentHistory,
  userProfile,
  onClose,
  onStatusChange,
  onTransfer,
  onUpdate,
  onReload,
  initialTab: initialTabProp
}) => {
  const { toast, confirm, prompt } = useModal();
  // Initialiser avec initialTabProp s'il est défini, sinon 'notes'
  // Utiliser une fonction pour s'assurer que la valeur initiale est correcte
  const [activeTab, setActiveTab] = useState<'notes' | 'documents' | 'history' | 'reminders' | 'sequence'>(() => {
    return initialTabProp || 'notes';
  });
  const [isEstimationModalOpen, setIsEstimationModalOpen] = useState(false);
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  
  // États pour reminders
  const [newReminderDate, setNewReminderDate] = useState('');
  const [newReminderTime, setNewReminderTime] = useState('');
  const [newReminderText, setNewReminderText] = useState('');
  const [newReminderHideUntilDate, setNewReminderHideUntilDate] = useState(false);
  
  // États pour sequences
  const [templates, setTemplates] = useState<SequenceTemplate[]>([]);
  
  // État local pour le lead (mis à jour après réaffectation)
  const [lead, setLead] = useState<MarketingLead>(leadProp);
  
  // État local pour l'historique d'affectation
  const [localAssignmentHistory, setLocalAssignmentHistory] = useState(assignmentHistory);
  
  // Mettre à jour le lead local quand le prop change
  useEffect(() => {
    setLead(leadProp);
    // Réinitialiser l'onglet quand le lead change si initialTabProp est défini
    if (initialTabProp) {
      setActiveTab(initialTabProp);
    }
  }, [leadProp, initialTabProp]);
  
  // Mettre à jour l'historique d'affectation quand le prop change
  useEffect(() => {
    setLocalAssignmentHistory(assignmentHistory);
  }, [assignmentHistory]);
  
  // Charger les templates de séquences marketing
  useEffect(() => {
    const loadTemplates = async () => {
      if (userProfile?.marketingProviderId) {
        const marketingTemplates = await storage.getMarketingSequenceTemplates(userProfile.marketingProviderId);
        setTemplates(marketingTemplates);
      }
    };
    loadTemplates();
  }, [userProfile?.marketingProviderId]);
  
  // Réinitialiser l'onglet si initialTabProp change ou au montage du composant
  useEffect(() => {
    if (initialTabProp) {
      setActiveTab(initialTabProp);
    }
  }, [initialTabProp]);
  
  // S'assurer que l'onglet est correctement défini au montage si initialTabProp est défini
  useEffect(() => {
    if (initialTabProp && activeTab !== initialTabProp) {
      setActiveTab(initialTabProp);
    }
  }, []); // Se déclencher uniquement au montage
  
  // États pour l'édition globale des blocs
  const [editingBlock, setEditingBlock] = useState<'contact' | 'property' | null>(null);
  const [contactForm, setContactForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [propertyForm, setPropertyForm] = useState({
    propertyAddress: '',
    propertyCity: '',
    propertyZipCode: '',
    propertyType: '',
    propertySurface: '',
    propertyRooms: '',
    projectType: ''
  });
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);

  // Fonctions pour l'édition globale des blocs
  const startEditingBlock = (block: 'contact' | 'property') => {
    setEditingBlock(block);
    if (block === 'contact') {
      setContactForm({
        firstName: lead.firstName || '',
        lastName: lead.lastName || '',
        email: lead.email || '',
        phone: lead.phone || ''
      });
    } else {
      setPropertyForm({
        propertyAddress: lead.propertyAddress || '',
        propertyCity: lead.propertyCity || '',
        propertyZipCode: lead.propertyZipCode || '',
        propertyType: lead.propertyType || '',
        propertySurface: lead.propertySurface?.toString() || '',
        propertyRooms: lead.propertyRooms?.toString() || '',
        projectType: lead.projectType || ''
      });
    }
  };

  const cancelEditingBlock = () => {
    setEditingBlock(null);
    setAddressSuggestions([]);
  };

  const saveBlock = async (block: 'contact' | 'property') => {
    if (block === 'contact') {
      await onUpdate(lead.id, {
        firstName: contactForm.firstName?.trim() || 'A définir',
        lastName: contactForm.lastName?.trim() || 'A définir',
        email: contactForm.email || null,
        phone: contactForm.phone || null
      });
    } else {
      await onUpdate(lead.id, {
        propertyAddress: propertyForm.propertyAddress || null,
        propertyCity: propertyForm.propertyCity || null,
        propertyZipCode: propertyForm.propertyZipCode || null,
        propertyType: propertyForm.propertyType || null,
        propertySurface: propertyForm.propertySurface ? parseFloat(propertyForm.propertySurface) : null,
        propertyRooms: propertyForm.propertyRooms ? parseInt(propertyForm.propertyRooms) : null,
        projectType: propertyForm.projectType || null
      });
    }
    setEditingBlock(null);
    setAddressSuggestions([]);
    onReload();
  };

  const StatusIcon = STATUS_LABELS[lead.status].icon;

  // Helper pour afficher le nom d'auteur
  const displayAuthorName = (noteAuthorName?: string): string => {
    if (!noteAuthorName || !userProfile) return noteAuthorName || '—';
    const currentUserName = userProfile.firstName && userProfile.lastName
      ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
      : userProfile.email?.split('@')[0] || '';
    return noteAuthorName === currentUserName ? 'Moi' : noteAuthorName;
  };

  // Fonction pour la recherche d'adresse (API GOUV)
  const handleAddressSearch = async (query: string) => {
    setPropertyForm({ ...propertyForm, propertyAddress: query });
    if (query.length > 3) {
      try {
        const res = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5`);
        const data = await res.json();
        setAddressSuggestions(data.features || []);
      } catch (e) {
        console.error(e);
        setAddressSuggestions([]);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleResendTransferEmail = async (leadId: string) => {
    setResendingEmail(leadId);
    try {
      const { success, error } = await storage.resendLeadTransferEmail(leadId);
      if (success) {
        toast.success('Email de notification réenvoyé avec succès');
      } else {
        toast.error('Erreur lors du réenvoi: ' + (error?.message || 'Erreur inconnue'));
      }
    } catch (error: any) {
      toast.error('Erreur lors du réenvoi: ' + (error?.message || 'Erreur inconnue'));
    } finally {
      setResendingEmail(null);
    }
  };
  
  const selectAddress = (feature: any) => {
    const address = feature.properties.name;
    const city = feature.properties.city;
    const zipCode = feature.properties.postcode;
    setPropertyForm({
      ...propertyForm,
      propertyAddress: address,
      propertyCity: city,
      propertyZipCode: zipCode
    });
    setAddressSuggestions([]);
  };
  
  // Fonctions pour gérer les reminders
  const handleAddReminder = async () => {
    if (!newReminderText.trim() || !newReminderDate) return;

    const hasTime = !!newReminderTime;
    // Si une heure est saisie => RDV (event Google Agenda), sinon => tâche (Google Tasks)
    const localIso = hasTime
      ? `${newReminderDate}T${newReminderTime}:00`
      : `${newReminderDate}T00:00:00`;
    const dateObj = new Date(localIso);
    const nowIso = new Date().toISOString();

    const newReminder: Reminder = {
      id: Date.now().toString(),
      date: dateObj.toISOString(),
      note: newReminderText,
      isDone: false,
      hideUntilDate: newReminderHideUntilDate,
      time: hasTime ? newReminderTime : undefined,
      startAt: hasTime ? dateObj.toISOString() : undefined,
      endAt: hasTime ? new Date(dateObj.getTime() + 60 * 60 * 1000).toISOString() : undefined,
      updatedAt: nowIso,
    };

    const updatedReminders = [...(lead.reminders || []), newReminder];
    await onUpdate(lead.id, { reminders: updatedReminders });
    setNewReminderText('');
    setNewReminderDate('');
    setNewReminderTime('');
    setNewReminderHideUntilDate(false);
    onReload();
  };
  
  const handleToggleReminder = async (reminderId: string) => {
    const updatedReminders = (lead.reminders || []).map(r => 
      r.id === reminderId ? { ...r, isDone: !r.isDone } : r
    );
    await onUpdate(lead.id, { reminders: updatedReminders });
    onReload();
  };
  
  const handleToggleHideUntilDate = async (reminderId: string) => {
    const updatedReminders = (lead.reminders || []).map(r => 
      r.id === reminderId ? { ...r, hideUntilDate: !r.hideUntilDate } : r
    );
    await onUpdate(lead.id, { reminders: updatedReminders });
    onReload();
  };
  
  const handleDeleteReminder = async (reminderId: string) => {
    if (await confirm({
      title: 'Supprimer le rappel',
      message: 'Êtes-vous sûr de vouloir supprimer ce rappel ?',
      danger: true
    })) {
      const updatedReminders = (lead.reminders || []).filter(r => r.id !== reminderId);
      await onUpdate(lead.id, { reminders: updatedReminders });
      onReload();
    }
  };
  
  // Fonctions pour gérer les sequences
  const handleStartSequence = async (template: SequenceTemplate) => {
    const today = new Date();
    const leadDate = new Date(lead.receivedAt || lead.createdAt);
    
    const newSteps: SequenceStep[] = template.steps.map((step, index) => {
      const stepDate = new Date(leadDate);
      stepDate.setDate(leadDate.getDate() + step.dayOffset);
      
      return {
        id: Date.now().toString() + index,
        type: step.type as StepType,
        dayOffset: step.dayOffset,
        date: stepDate.toISOString(),
        title: step.title,
        content: step.content,
        status: 'PENDING'
      };
    });
    
    // Ajouter une note
    const noteContent = `🚀 Séquence "${template.name}" démarrée`;
    await storage.addMarketingLeadNote(lead.id, noteContent);
    
    await onUpdate(lead.id, { sequenceSteps: newSteps });
    setActiveTab('sequence');
    toast.success('Séquence démarrée !');
    onReload();
  };
  
  const handleExecuteStep = (step: SequenceStep) => {
    const userName = userProfile 
      ? (userProfile.firstName && userProfile.lastName 
          ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
          : userProfile.email?.split('@')[0] || 'L\'équipe Placid')
      : 'L\'équipe Placid';
    
    // Préparer le contenu avec remplacements
    let body = step.content
      .replace(/\[PRENOM\]/g, lead.firstName || '')
      .replace(/\[NOM\]/g, lead.lastName || '')
      .replace(/\[ADRESSE\]/g, lead.propertyAddress || '')
      .replace(/\[VILLE\]/g, lead.propertyCity || '')
      .replace(/\[NOM_USER\]/g, userName);
    
    // Ouvrir l'application externe
    if (step.type === 'EMAIL') {
      const subject = encodeURIComponent(`Concernant votre bien à ${lead.propertyCity || ''}`);
      window.open(`mailto:${lead.email}?subject=${subject}&body=${encodeURIComponent(body)}`);
    } else if (step.type === 'WHATSAPP') {
      let phone = (lead.phone || '').replace(/[^\d]/g, '').replace(/^33/, '');
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(body)}`);
    } else if (step.type === 'SMS') {
      const phone = (lead.phone || '').replace(/[^\d]/g, '');
      window.open(`sms:${phone}?body=${encodeURIComponent(body)}`);
    } else if (step.type === 'CALL') {
      const phone = (lead.phone || '').replace(/[^\d]/g, '');
      window.open(`tel:${phone}`);
    }
    
    // Marquer comme envoyé
    const updatedSteps = (lead.sequenceSteps || []).map(s => 
      s.id === step.id ? { ...s, status: 'SENT' as StepStatus } : s
    );
    onUpdate(lead.id, { sequenceSteps: updatedSteps });
    onReload();
  };
  
  const handleSkipStep = async (stepId: string) => {
    const updatedSteps = (lead.sequenceSteps || []).map(s => 
      s.id === stepId ? { ...s, status: 'SKIPPED' as StepStatus } : s
    );
    await onUpdate(lead.id, { sequenceSteps: updatedSteps });
    onReload();
  };
  
  const handleResetSequence = async () => {
    if (await confirm({
      title: 'Arrêter la séquence',
      message: 'Voulez-vous vraiment arrêter la séquence en cours ? Les tâches prévues seront supprimées.',
      danger: true
    })) {
      await onUpdate(lead.id, { sequenceSteps: [] });
      toast.info('Séquence arrêtée.');
      onReload();
    }
  };
  
  // Tri des reminders et sequence steps
  const sortedReminders = [...(lead.reminders || [])].sort((a, b) => {
    if (a.isDone === b.isDone) return new Date(a.date).getTime() - new Date(b.date).getTime();
    return a.isDone ? 1 : -1;
  });
  const sequenceSteps = lead.sequenceSteps || [];
  const hasCancelledSteps = sequenceSteps.some(s => s.status === 'CANCELLED');

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-6xl max-h-[90vh] shadow-2xl flex flex-col rounded-2xl overflow-y-auto animate-in zoom-in-95 duration-200">
        {/* HEADER */}
        <div className="p-6 border-b border-slate-100 bg-white shrink-0 z-10 flex flex-col gap-4">
          <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-xl">
              <TrendingUp className="w-6 h-6 text-amber-600" />
            </div>
            <div>
                <h2 className="text-2xl font-bold text-slate-900 leading-tight">
                {lead.firstName && lead.firstName !== 'A définir' ? lead.firstName : ''} {lead.lastName && lead.lastName !== 'A définir' ? lead.lastName : ''}
                {(!lead.firstName || lead.firstName === 'A définir') && (!lead.lastName || lead.lastName === 'A définir') && 'A définir'}
              </h2>
                <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                {lead.source === 'meta' && lead.metaLeadId ? (
                  <>
                    <Facebook className="w-3.5 h-3.5" />
                      <span>ID Meta: {lead.metaLeadId}</span>
                  </>
                ) : (
                  <>
                    {React.createElement(SOURCE_LABELS[lead.source]?.icon || SOURCE_LABELS.other.icon, { className: 'w-3.5 h-3.5' })}
                      <span>{SOURCE_LABELS[lead.source]?.label || lead.source || SOURCE_LABELS.other.label}</span>
                  </>
                )}
                  <span>•</span>
                  <span>Reçu le {new Date(lead.receivedAt).toLocaleDateString()}</span>
                </div>
            </div>
          </div>
          <button
            onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
              <X className="w-6 h-6 text-slate-500" />
          </button>
        </div>

          {/* STATUT ACTUEL + INFOS */}
          <div className="w-full bg-slate-50 p-4 rounded-xl space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${STATUS_LABELS[lead.status].color.replace('text-', 'bg-').replace('bg-', 'bg-').replace('800', '100')}`}>
                  <StatusIcon className={`w-5 h-5 ${STATUS_LABELS[lead.status].color.split(' ')[1]}`} />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase mb-0.5">Statut actuel</div>
                  <span className={`px-3 py-1.5 rounded-full text-sm font-bold ${STATUS_LABELS[lead.status].color} shadow-sm`}>
                    {STATUS_LABELS[lead.status].label}
                  </span>
                </div>
              </div>
              {lead.status !== 'transfere' && lead.status !== 'rejete' && (
                <div className="flex gap-2 flex-wrap">
                  {lead.status !== 'nouveau' && (
                    <button
                      onClick={() => onStatusChange(lead.id, 'nouveau')}
                      className="px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg hover:bg-slate-50 font-medium transition-colors shadow-sm"
                    >
                      Nouveau
                    </button>
                  )}
                  {lead.status !== 'en_qualification' && (
                    <button
                      onClick={() => onStatusChange(lead.id, 'en_qualification')}
                      className="px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg hover:bg-slate-50 font-medium transition-colors shadow-sm"
                    >
                      Qualification
                    </button>
                  )}
                  {lead.status === 'en_qualification' && (
                    <button
                      onClick={() => onStatusChange(lead.id, 'qualifie')}
                      className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium shadow-sm transition-colors"
                    >
                      Qualifier
                    </button>
                  )}
                  {lead.status === 'qualifie' && (
                    <button
                      onClick={() => onTransfer(lead)}
                      className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 font-medium shadow-sm transition-colors"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Transférer
                    </button>
                  )}
                  <button
                    onClick={() => onStatusChange(lead.id, 'rejete')}
                    className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-sm transition-colors"
                  >
                    Rejeter
                  </button>
                </div>
              )}
            </div>
            
            {/* Campagne, Affectation, Type de projet */}
            <div className="flex items-center gap-4 flex-wrap pt-2 border-t border-slate-200">
              {lead.campaignName && (
                <div className="flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-slate-600">
                    <span className="font-semibold">Campagne:</span> {lead.campaignName}
                  </span>
                </div>
              )}
              {(() => {
                const currentUserId = userProfile?.id;
                const isSupervisor = userProfile?.isSupervisor;
                const isAssignedToCurrentUser = lead.assignedToUserId === currentUserId;
                const canReassign = (isSupervisor || isAssignedToCurrentUser || !lead.assignedToUserId) && (userProfile?.role === 'prestataire_marketing' || userProfile?.role === 'superadmin' || userProfile?.role === 'admin');
                
                return (
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-slate-600">
                      {lead.assignedToUserId ? (
                        <>
                          <span className="font-semibold">Affecté à:</span>{' '}
                          {(() => {
                            const setter = profiles.find(p => p.id === lead.assignedToUserId);
                            return setter ? `${setter.firstName || ''} ${setter.lastName || ''}`.trim() || setter.email : '—';
                          })()}
                        </>
                      ) : (
                        <>
                          <span className="font-semibold">Non affecté</span>
                        </>
                      )}
                    </span>
                    {canReassign && (
                      <button
                        onClick={() => setIsReassignModalOpen(true)}
                        className="px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 font-medium transition-colors"
                      >
                        {lead.assignedToUserId ? 'Réaffecter' : 'Affecter'}
                      </button>
                    )}
                  </div>
                );
              })()}
              {lead.projectType && (
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-slate-600">
                    <span className="font-semibold">Type:</span> {lead.projectType}
                  </span>
                </div>
              )}
              {lead.annonceUrl && (
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-blue-500" />
                  <a
                    href={lead.annonceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 underline font-semibold"
                  >
                    Voir l'annonce
                  </a>
                </div>
              )}
            </div>
          </div>

        <div className="flex-1 min-h-0 overflow-hidden grid grid-cols-12 bg-slate-50/50">
            
            {/* LEFT SIDEBAR - CONTEXT */}
            <div className="col-span-4 border-r border-slate-200 bg-white overflow-y-auto p-6 space-y-6 min-h-0">
              
              {/* ACTION BUTTONS GRID */}
              <div className="grid grid-cols-2 gap-3">
                {lead.status !== 'transfere' && lead.status !== 'rejete' && (
                  <>
                    {lead.status === 'en_qualification' && (
                      <button
                        onClick={() => onStatusChange(lead.id, 'qualifie')}
                        className="col-span-2 flex items-center justify-center p-3 bg-green-50 hover:bg-green-100 border border-green-200 hover:border-green-300 rounded-xl text-green-800 transition-all font-bold group"
                      >
                        <CheckCircle2 className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                        QUALIFIER
                      </button>
                    )}
                    {lead.status === 'qualifie' && (
                      <button
                        onClick={() => onTransfer(lead)}
                        className="col-span-2 flex items-center justify-center p-3 bg-purple-50 hover:bg-purple-100 border border-purple-200 hover:border-purple-300 rounded-xl text-purple-800 transition-all font-bold group"
                      >
                        <Send className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                        TRANSFÉRER À UNE AGENCE
                      </button>
                    )}
                    <button
                      onClick={() => onStatusChange(lead.id, 'rejete')}
                      className="col-span-2 flex items-center justify-center p-3 bg-red-50 hover:bg-red-100 border border-red-200 hover:border-red-300 rounded-xl text-red-800 transition-all font-bold group"
                    >
                      <XCircle className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                      REJETER
                    </button>
                  </>
                )}
              </div>

              {/* CONTACT CARD */}
              <div className="border rounded-xl p-5 shadow-sm bg-white border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center">
                    <UserCheck className="w-4 h-4 mr-2" /> Contact
                  </h3>
                  {editingBlock !== 'contact' && (
                    <button
                      onClick={() => startEditingBlock('contact')}
                      className="p-1.5 text-slate-400 hover:text-amber-500 transition-colors"
                      title="Modifier"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {editingBlock === 'contact' ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-bold text-slate-500 mb-1 uppercase block">Prénom</label>
                      <input
                        type="text"
                        value={contactForm.firstName}
                        onChange={(e) => setContactForm({ ...contactForm, firstName: e.target.value })}
                        className="w-full px-3 py-2 border border-amber-500 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 mb-1 uppercase block">Nom</label>
                      <input
                        type="text"
                        value={contactForm.lastName}
                        onChange={(e) => setContactForm({ ...contactForm, lastName: e.target.value })}
                        className="w-full px-3 py-2 border border-amber-500 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 mb-1 uppercase block">Email</label>
                      <input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-amber-500 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 mb-1 uppercase block">Téléphone</label>
                      <input
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-amber-500 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900 text-sm"
                      />
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => saveBlock('contact')}
                        className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium transition-colors"
                      >
                        Enregistrer
                      </button>
                      <button
                        onClick={cancelEditingBlock}
                        className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 font-medium transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <div>
                        <div className="text-xs font-bold text-slate-500 mb-1 uppercase">Prénom</div>
                        <div className="font-medium text-slate-900 text-sm">{lead.firstName && lead.firstName !== 'A définir' ? lead.firstName : 'A définir'}</div>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-500 mb-1 uppercase">Nom</div>
                        <div className="font-medium text-slate-900 text-sm">{lead.lastName && lead.lastName !== 'A définir' ? lead.lastName : 'A définir'}</div>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-500 mb-1 uppercase">Email</div>
                        <div className="font-medium text-slate-900 text-sm flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {lead.email || '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-500 mb-1 uppercase">Téléphone</div>
                        <div className="font-medium text-slate-900 text-sm flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400" />
                          {lead.phone || '—'}
                        </div>
                      </div>
                    </div>
                    {lead.email && (
                      <div className="flex space-x-2 mt-4">
                        <a href={`mailto:${lead.email}`} className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 rounded-lg flex items-center justify-center text-xs font-bold transition-colors border border-blue-200">
                          <Mail className="w-3.5 h-3.5 mr-2" /> Mail
                        </a>
                        {lead.phone && (
                          <a href={`tel:${lead.phone}`} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2 rounded-lg flex items-center justify-center text-xs font-bold transition-colors">
                            <Phone className="w-3.5 h-3.5 mr-2" /> Appel
                          </a>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* PROPERTY SPECS */}
              <div className="border rounded-xl p-5 shadow-sm bg-white border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center">
                    <Building className="w-4 h-4 mr-2" /> Bien immobilier
                  </h3>
                  {editingBlock !== 'property' && (
                    <button
                      onClick={() => startEditingBlock('property')}
                      className="p-1.5 text-slate-400 hover:text-amber-500 transition-colors"
                      title="Modifier"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {editingBlock === 'property' ? (
                  <div className="space-y-3">
                    <div className="relative">
                      <label className="text-xs font-bold text-slate-500 mb-1 uppercase block">Adresse</label>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          value={propertyForm.propertyAddress}
                          onChange={(e) => handleAddressSearch(e.target.value)}
                          className="flex-1 px-3 py-2 border border-amber-500 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900 text-sm"
                          placeholder="Recherche auto..."
                        />
                      </div>
                      {addressSuggestions.length > 0 && (
                        <ul className="absolute z-10 w-full bg-white border border-slate-200 rounded-xl mt-2 shadow-xl max-h-48 overflow-auto">
                          {addressSuggestions.map((s, i) => (
                            <li 
                              key={i} 
                              onClick={() => selectAddress(s)} 
                              className="p-3 hover:bg-slate-50 cursor-pointer text-sm border-b border-slate-50 last:border-0 text-slate-900"
                            >
                              <span className="font-bold block">{s.properties.name}</span>
                              <span className="text-slate-500 text-xs">{s.properties.postcode} {s.properties.city}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-bold text-slate-500 mb-1 uppercase block">Ville</label>
                        <input
                          type="text"
                          value={propertyForm.propertyCity}
                          onChange={(e) => setPropertyForm({ ...propertyForm, propertyCity: e.target.value })}
                          className="w-full px-3 py-2 border border-amber-500 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-slate-500 mb-1 uppercase block">Code postal</label>
                        <input
                          type="text"
                          value={propertyForm.propertyZipCode}
                          onChange={(e) => setPropertyForm({ ...propertyForm, propertyZipCode: e.target.value })}
                          className="w-full px-3 py-2 border border-amber-500 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 mb-1 uppercase block">Type de logement</label>
                      <SearchableSelect
                        options={[
                          { value: 'Appartement', label: 'Appartement' },
                          { value: 'Maison', label: 'Maison' },
                          { value: 'Immeuble', label: 'Immeuble' },
                          { value: 'Autre', label: 'Autre' }
                        ]}
                        value={propertyForm.propertyType}
                        onChange={(value) => setPropertyForm({ ...propertyForm, propertyType: value })}
                        placeholder="Type de logement"
                        icon={<Building className="w-4 h-4" />}
                        showAllOnOpen={true}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-bold text-slate-500 mb-1 uppercase block">Surface (m²)</label>
                        <input
                          type="number"
                          value={propertyForm.propertySurface}
                          onChange={(e) => setPropertyForm({ ...propertyForm, propertySurface: e.target.value })}
                          className="w-full px-3 py-2 border border-amber-500 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-slate-500 mb-1 uppercase block">Pièces</label>
                        <input
                          type="number"
                          value={propertyForm.propertyRooms}
                          onChange={(e) => setPropertyForm({ ...propertyForm, propertyRooms: e.target.value })}
                          className="w-full px-3 py-2 border border-amber-500 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-slate-900 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 mb-1 uppercase block">Type de projet</label>
                      <SearchableSelect
                        options={[
                          { value: 'Conciergerie', label: 'Conciergerie' },
                          { value: 'Sous-location', label: 'Sous-location' }
                        ]}
                        value={propertyForm.projectType}
                        onChange={(value) => setPropertyForm({ ...propertyForm, projectType: value })}
                        placeholder="Sélectionner..."
                        icon={<FileText className="w-4 h-4" />}
                        showAllOnOpen={true}
                      />
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => saveBlock('property')}
                        className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium transition-colors"
                      >
                        Enregistrer
                      </button>
                      <button
                        onClick={cancelEditingBlock}
                        className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 font-medium transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <div>
                        <div className="text-xs font-bold text-slate-500 mb-1 uppercase">Adresse</div>
                        <div className="font-medium text-slate-900 text-sm flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          {lead.propertyAddress || '—'}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-xs font-bold text-slate-500 mb-1 uppercase">Ville</div>
                          <div className="font-medium text-slate-900 text-sm">{lead.propertyCity || '—'}</div>
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-500 mb-1 uppercase">Code postal</div>
                          <div className="font-medium text-slate-900 text-sm">{lead.propertyZipCode || '—'}</div>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-500 mb-1 uppercase">Type de logement</div>
                        <div className="font-medium text-slate-900 text-sm">{lead.propertyType || '—'}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-xs font-bold text-slate-500 mb-1 uppercase">Surface (m²)</div>
                          <div className="font-medium text-slate-900 text-sm">{lead.propertySurface ? `${lead.propertySurface}m²` : '—'}</div>
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-500 mb-1 uppercase">Pièces</div>
                          <div className="font-medium text-slate-900 text-sm">{lead.propertyRooms || '—'}</div>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-500 mb-1 uppercase">Type de projet</div>
                        <div className="font-medium text-slate-900 text-sm">{lead.projectType || '—'}</div>
                      </div>
                    </div>
                    {lead.propertyAddress && lead.propertySurface && lead.propertyRooms && (
                      <button
                        onClick={() => setIsEstimationModalOpen(true)}
                        className="w-full mt-4 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium shadow-sm transition-colors flex items-center justify-center gap-2"
                      >
                        <Calculator className="w-4 h-4" />
                        Estimation
                      </button>
                    )}
                  </>
                )}
              </div>


              {/* TRANSFERT (si transféré) */}
              {lead.status === 'transfere' && lead.agencyId && (
                <div className="border rounded-xl p-5 shadow-sm bg-white border-purple-200 bg-purple-50">
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center mb-4">
                    <Building className="w-4 h-4 mr-2" /> Transfert
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Agence</span>
                      <span className="font-medium text-slate-900">
                        {agencies.find(a => a.id === lead.agencyId)?.name || agencies.find(a => a.id === lead.agencyId)?.city || 'Agence inconnue'}
                      </span>
                    </div>
                    {lead.transferredAt && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Date</span>
                        <span className="font-medium text-slate-900">
                          {new Date(lead.transferredAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {(userProfile?.role === 'admin' || userProfile?.role === 'superadmin') && (
                      <div className="pt-3 border-t border-purple-200">
                        <button
                          onClick={() => handleResendTransferEmail(lead.id)}
                          disabled={resendingEmail === lead.id}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white text-sm font-semibold rounded-lg transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          {resendingEmail === lead.id ? 'Envoi en cours...' : 'Réenvoyer l\'email'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT MAIN CONTENT */}
            <div className="col-span-8 flex flex-col min-h-0 overflow-hidden">
                
                {/* TABS */}
                <div className="flex border-b border-slate-200 bg-white px-6">
                    <button 
                        onClick={() => setActiveTab('notes')} 
                        className={`py-4 px-4 text-sm font-bold border-b-2 transition-colors ${activeTab === 'notes' ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        Notes
                    </button>
                    <button 
                        onClick={() => setActiveTab('reminders')} 
                        className={`py-4 px-4 text-sm font-bold border-b-2 transition-colors flex items-center ${activeTab === 'reminders' ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        Tâches
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${activeTab === 'reminders' ? 'bg-amber-100' : 'bg-slate-100'}`}>
                            {lead.reminders?.filter(r => !r.isDone).length || 0}
                        </span>
                    </button>
                    <button 
                        onClick={() => setActiveTab('sequence')} 
                        className={`py-4 px-4 text-sm font-bold border-b-2 transition-colors flex items-center ${activeTab === 'sequence' ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        Séquence Auto
                        {sequenceSteps.length > 0 && !hasCancelledSteps && (
                            <span className="ml-2 w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                        )}
                    </button>
                    <button 
                        onClick={() => setActiveTab('documents')} 
                        className={`py-4 px-4 text-sm font-bold border-b-2 transition-colors flex items-center ${activeTab === 'documents' ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        Documents
                        {lead.documents && lead.documents.length > 0 && (
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${activeTab === 'documents' ? 'bg-amber-100' : 'bg-slate-100'}`}>
                                {lead.documents.length}
                            </span>
                        )}
                    </button>
                    <button 
                        onClick={() => setActiveTab('history')} 
                        className={`py-4 px-4 text-sm font-bold border-b-2 transition-colors flex items-center ${activeTab === 'history' ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        Historique
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-6">
                    
                    {/* TAB: NOTES */}
                    {activeTab === 'notes' && (
                        <div className="space-y-6 max-w-3xl mx-auto">
                            <NotesSection lead={lead} onReload={onReload} userProfile={userProfile} />
                        </div>
                    )}
                    
                    {/* TAB: REMINDERS */}
                    {activeTab === 'reminders' && (
                        <div className="space-y-6 max-w-3xl mx-auto">
                            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                                <h4 className="font-bold text-slate-800 mb-3 text-sm uppercase">Nouveau rappel</h4>
                                <div className="flex flex-col space-y-3">
                                    <input 
                                        type="text" 
                                        className="w-full outline-none text-sm p-3 bg-slate-50 text-slate-900 rounded-lg border border-slate-200 focus:border-amber-500 transition-colors" 
                                        placeholder="Que faut-il faire ?" 
                                        value={newReminderText} 
                                        onChange={e => setNewReminderText(e.target.value)} 
                                    />
                                    <div className="flex items-center space-x-3">
                                        <div className="relative flex-1">
                                            <Calendar className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                                            <input 
                                                type="date" 
                                                className="text-sm border border-slate-200 rounded-lg p-2 pl-9 focus:border-amber-500 outline-none bg-white w-full text-slate-700" 
                                                value={newReminderDate} 
                                                onChange={e => setNewReminderDate(e.target.value)} 
                                            />
                                        </div>
                                        <div className="relative w-36">
                                            <input
                                                type="time"
                                                className="text-sm border border-slate-200 rounded-lg p-2 focus:border-amber-500 outline-none bg-white w-full text-slate-700"
                                                value={newReminderTime}
                                                onChange={e => setNewReminderTime(e.target.value)}
                                                placeholder="Heure (optionnel)"
                                                title="Avec une heure : RDV (Google Agenda). Sans heure : tâche (Google Tasks)."
                                            />
                                        </div>
                                        <button 
                                            onClick={handleAddReminder} 
                                            disabled={!newReminderText.trim() || !newReminderDate} 
                                            className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-lg text-sm font-bold flex items-center disabled:opacity-50 transition-colors shadow-sm"
                                        >
                                            <Plus className="w-4 h-4 mr-2" /> Ajouter
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-400">Heure renseignée → RDV. Sans heure → tâche.</p>
                                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-slate-900">
                                        <input
                                            type="checkbox"
                                            checked={newReminderHideUntilDate}
                                            onChange={(e) => setNewReminderHideUntilDate(e.target.checked)}
                                            className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 focus:ring-offset-0"
                                        />
                                        <span>Masquer le lead jusqu'à la date du rappel</span>
                                    </label>
                                </div>
                            </div>
                            
                            <div className="space-y-3">
                                {sortedReminders.map((reminder) => {
                                    const reminderDate = new Date(reminder.date);
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    reminderDate.setHours(0, 0, 0, 0);
                                    const isLate = reminderDate < today && !reminder.isDone;
                                    const isToday = reminderDate.getTime() === today.getTime();
                                    
                                    return (
                                        <div 
                                            key={reminder.id} 
                                            className={`bg-white p-4 rounded-xl shadow-sm border ${isLate ? 'border-red-200 bg-red-50' : isToday ? 'border-amber-200 bg-amber-50' : 'border-slate-200'}`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-start space-x-3 flex-1">
                                                    <button
                                                        onClick={() => handleToggleReminder(reminder.id)}
                                                        className={`mt-1 p-1 rounded border-2 transition-colors ${reminder.isDone ? 'bg-green-500 border-green-500' : 'border-slate-300 hover:border-amber-500'}`}
                                                    >
                                                        {reminder.isDone && <CheckSquare className="w-4 h-4 text-white" />}
                                                    </button>
                                                    <div className="flex-1">
                                                        <div className="flex items-center space-x-2 mb-1">
                                                            <Calendar className={`w-4 h-4 ${isLate ? 'text-red-600' : isToday ? 'text-amber-600' : 'text-slate-400'}`} />
                                                            <span className={`text-sm font-medium ${reminder.isDone ? 'line-through text-slate-400' : isLate ? 'text-red-700' : isToday ? 'text-amber-700' : 'text-slate-700'}`}>
                                                                {new Date(reminder.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                                                {reminder.time ? ` ${reminder.time}` : ''}
                                                            </span>
                                                            <span className={`px-2 py-0.5 text-xs font-bold rounded ${reminder.time ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                                                                {reminder.time ? 'RDV' : 'Tâche'}
                                                            </span>
                                                            {isLate && !reminder.isDone && (
                                                                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">En retard</span>
                                                            )}
                                                            {isToday && !reminder.isDone && (
                                                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded">Aujourd'hui</span>
                                                            )}
                                                            {reminder.hideUntilDate && (
                                                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded flex items-center gap-1">
                                                                    <EyeOff className="w-3 h-3" />
                                                                    Masqué
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <p className={`text-sm flex-1 ${reminder.isDone ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                                                                {reminder.note}
                                                            </p>
                                                            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer hover:text-slate-900">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={reminder.hideUntilDate || false}
                                                                    onChange={() => handleToggleHideUntilDate(reminder.id)}
                                                                    className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 focus:ring-offset-0"
                                                                />
                                                                <span>Masquer le lead jusqu'à la date du rappel</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleDeleteReminder(reminder.id)}
                                                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                                {sortedReminders.length === 0 && (
                                    <p className="text-slate-400 italic text-center py-8">Aucune tâche pour le moment.</p>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {/* TAB: SEQUENCE */}
                    {activeTab === 'sequence' && (
                        <div className="space-y-6 max-w-3xl mx-auto">
                            {sequenceSteps.length === 0 ? (
                                <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 text-center">
                                    <Zap className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                                    <h4 className="font-bold text-slate-800 mb-2">Aucune séquence active</h4>
                                    <p className="text-sm text-slate-500 mb-6">Démarrez une séquence automatisée pour ce lead</p>
                                    
                                    {templates.length > 0 ? (
                                        <div className="space-y-2">
                                            {templates.map((template) => (
                                                <button
                                                    key={template.id}
                                                    onClick={() => handleStartSequence(template)}
                                                    className="w-full p-4 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-left transition-colors flex items-center justify-between"
                                                >
                                                    <div className="flex items-center space-x-3">
                                                        <span className="text-2xl">{template.icon || '📧'}</span>
                                                        <div>
                                                            <div className="font-bold text-slate-900">{template.name}</div>
                                                            {template.description && (
                                                                <div className="text-xs text-slate-500">{template.description}</div>
                                                            )}
                                                            <div className="text-xs text-slate-400 mt-1">
                                                                {template.steps.length} étape{template.steps.length > 1 ? 's' : ''}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Play className="w-5 h-5 text-amber-600" />
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-400">Aucun template de séquence disponible. Contactez votre administrateur.</p>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex items-center justify-between">
                                        <div>
                                            <h4 className="font-bold text-slate-800">Séquence en cours</h4>
                                            <p className="text-sm text-slate-500">
                                                {sequenceSteps.filter(s => s.status === 'PENDING').length} étape{sequenceSteps.filter(s => s.status === 'PENDING').length > 1 ? 's' : ''} en attente
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleResetSequence}
                                            className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm font-bold transition-colors flex items-center"
                                        >
                                            <Ban className="w-4 h-4 mr-2" /> Arrêter
                                        </button>
                                    </div>
                                    
                                    <div className="space-y-3">
                                        {sequenceSteps.map((step) => {
                                            const stepDate = new Date(step.date);
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            stepDate.setHours(0, 0, 0, 0);
                                            const isLate = stepDate < today && step.status === 'PENDING';
                                            const isToday = stepDate.getTime() === today.getTime();
                                            
                                            let icon = null;
                                            if (step.type === 'EMAIL') icon = <Mail className="w-4 h-4" />;
                                            else if (step.type === 'CALL') icon = <Phone className="w-4 h-4" />;
                                            else if (step.type === 'WHATSAPP') icon = <MessageCircle className="w-4 h-4" />;
                                            else if (step.type === 'SMS') icon = <MessageSquare className="w-4 h-4" />;
                                            else icon = <Zap className="w-4 h-4" />;
                                            
                                            return (
                                                <div 
                                                    key={step.id} 
                                                    className={`bg-white p-4 rounded-xl shadow-sm border ${
                                                        step.status === 'SENT' ? 'border-green-200 bg-green-50' :
                                                        step.status === 'SKIPPED' ? 'border-slate-200 bg-slate-50' :
                                                        step.status === 'CANCELLED' ? 'border-red-200 bg-red-50' :
                                                        isLate ? 'border-red-200 bg-red-50' :
                                                        isToday ? 'border-amber-200 bg-amber-50' :
                                                        'border-slate-200'
                                                    }`}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-start space-x-3 flex-1">
                                                            <div className={`mt-1 p-2 rounded-lg ${
                                                                step.status === 'SENT' ? 'bg-green-100' :
                                                                step.status === 'SKIPPED' ? 'bg-slate-100' :
                                                                step.status === 'CANCELLED' ? 'bg-red-100' :
                                                                'bg-amber-100'
                                                            }`}>
                                                                {icon}
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center space-x-2 mb-1">
                                                                    <span className={`text-sm font-bold ${
                                                                        step.status === 'SENT' ? 'text-green-700' :
                                                                        step.status === 'SKIPPED' ? 'text-slate-500' :
                                                                        step.status === 'CANCELLED' ? 'text-red-700' :
                                                                        isLate ? 'text-red-700' :
                                                                        isToday ? 'text-amber-700' :
                                                                        'text-slate-700'
                                                                    }`}>
                                                                        {step.title}
                                                                    </span>
                                                                    {step.status === 'PENDING' && isLate && (
                                                                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">En retard</span>
                                                                    )}
                                                                    {step.status === 'PENDING' && isToday && (
                                                                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded">Aujourd'hui</span>
                                                                    )}
                                                                    {step.status === 'SENT' && (
                                                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">Envoyé</span>
                                                                    )}
                                                                    {step.status === 'SKIPPED' && (
                                                                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-bold rounded">Ignoré</span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center space-x-2 mb-2">
                                                                    <Calendar className="w-3 h-3 text-slate-400" />
                                                                    <span className="text-xs text-slate-500">
                                                                        {new Date(step.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                                                    </span>
                                                                </div>
                                                                <p className="text-sm text-slate-600 whitespace-pre-wrap">{step.content}</p>
                                                            </div>
                                                        </div>
                                                        {step.status === 'PENDING' && (
                                                            <div className="flex items-center space-x-2">
                                                                <button
                                                                    onClick={() => handleExecuteStep(step)}
                                                                    className="p-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
                                                                    title="Exécuter"
                                                                >
                                                                    <Play className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSkipStep(step.id)}
                                                                    className="p-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
                                                                    title="Ignorer"
                                                                >
                                                                    <SkipForward className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    
                    {/* TAB: DOCUMENTS */}
                    {activeTab === 'documents' && (
                        <div className="space-y-6 max-w-3xl mx-auto">
                            <DocumentsSection lead={lead} onReload={onReload} />
                        </div>
                    )}

                    {/* TAB: HISTORY */}
                    {activeTab === 'history' && (
                        <div className="space-y-6 max-w-3xl mx-auto">
                            <div className="space-y-4 relative pl-6 border-l-2 border-slate-200 ml-4">
                                {(() => {
                                    // Combiner les deux historiques et les trier par date
                                    const combinedHistory = [
                                        ...statusHistory.map(entry => ({ ...entry, type: 'status' as const })),
                                        ...localAssignmentHistory.map(entry => ({ ...entry, type: 'assignment' as const }))
                                    ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

                                    return combinedHistory.map((entry) => {
                                        if (entry.type === 'status') {
                                            const statusEntry = entry as MarketingLeadStatusHistory & { type: 'status' };
                                            return (
                                                <div key={statusEntry.id} className="relative group">
                                                    <div className="absolute -left-[31px] top-4 w-4 h-4 rounded-full bg-white border-2 border-slate-300 group-hover:border-amber-500 transition-colors"></div>
                                                    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 text-sm">
                                                        <div className="flex justify-between items-start mb-3">
                                                            <div className="flex items-center gap-2">
                                                                <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${STATUS_LABELS[statusEntry.newStatus as MarketingLeadStatus]?.color || 'bg-slate-200 text-slate-700'}`}>
                                                                    {STATUS_LABELS[statusEntry.newStatus as MarketingLeadStatus]?.label || statusEntry.newStatus}
                                                                </span>
                                                                {statusEntry.oldStatus && (
                                                                    <>
                                                                        <ArrowRight className="w-4 h-4 text-slate-400" />
                                                                        <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${STATUS_LABELS[statusEntry.oldStatus as MarketingLeadStatus]?.color || 'bg-slate-200 text-slate-700'}`}>
                                                                            {STATUS_LABELS[statusEntry.oldStatus as MarketingLeadStatus]?.label || statusEntry.oldStatus}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <span className="text-xs text-slate-400 bg-slate-50 px-2 py-1 rounded-full">{new Date(statusEntry.createdAt).toLocaleString()}</span>
                                                        </div>
                                                        {statusEntry.comment && (
                                                            <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{statusEntry.comment}</p>
                                                        )}
                                                        <div className="mt-2 text-xs text-slate-500">
                                                            Par: {getAssignedByUserName(statusEntry.changedBy, profiles)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        } else {
                                            const assignmentEntry = entry as typeof assignmentHistory[0] & { type: 'assignment' };
                                            return (
                                                <div key={assignmentEntry.id} className="relative group">
                                                    <div className="absolute -left-[31px] top-4 w-4 h-4 rounded-full bg-white border-2 border-amber-300 group-hover:border-amber-500 transition-colors"></div>
                                                    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 text-sm">
                                                        <div className="flex justify-between items-start mb-3">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <UserCheck className="w-4 h-4 text-amber-600" />
                                                                <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                                                                    {assignmentEntry.assignmentMethod === 'reassigned' ? 'Réaffectation' : assignmentEntry.assignmentMethod === 'manual' ? 'Affectation manuelle' : 'Affectation automatique'}
                                                                </span>
                                                                {assignmentEntry.previousAssignedToUserId && (
                                                                    <>
                                                                        <ArrowRight className="w-4 h-4 text-slate-400" />
                                                                        <span className="text-xs text-slate-600">
                                                                            {getAssignedByUserName(assignmentEntry.previousAssignedToUserId, profiles)}
                                                                        </span>
                                                                    </>
                                                                )}
                                                                {assignmentEntry.assignedToUserId && (
                                                                    <>
                                                                        <ArrowRight className="w-4 h-4 text-slate-400" />
                                                                        <span className="text-xs text-slate-600 font-medium">
                                                                            {getAssignedByUserName(assignmentEntry.assignedToUserId, profiles)}
                                                                        </span>
                                                                    </>
                                                                )}
                                                                {!assignmentEntry.assignedToUserId && (
                                                                    <span className="text-xs text-red-600 font-medium">Non affecté</span>
                                                                )}
                                                            </div>
                                                            <span className="text-xs text-slate-400 bg-slate-50 px-2 py-1 rounded-full">{new Date(assignmentEntry.createdAt).toLocaleString()}</span>
                                                        </div>
                                                        {assignmentEntry.reason && (
                                                            <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{assignmentEntry.reason}</p>
                                                        )}
                                                        <div className="mt-2 text-xs text-slate-500">
                                                            {assignmentEntry.assignedByUserId ? (
                                                                <>Par: {getAssignedByUserName(assignmentEntry.assignedByUserId, profiles)}</>
                                                            ) : (
                                                                <>Affectation automatique</>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                    });
                                })()}
                                {statusHistory.length === 0 && localAssignmentHistory.length === 0 && (
                                    <p className="text-slate-400 italic pl-4">Aucun historique pour le moment.</p>
                                )}
                            </div>
                        </div>
                    )}

                </div>
            </div>
          </div>
        </div>

      </div>

      {/* Modal d'estimation */}
      {isEstimationModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-amber-50 to-white border-b border-slate-200 p-6 flex items-center justify-between z-10 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Calculator className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Estimation des revenus</h2>
                  <p className="text-sm text-slate-500 mt-0.5">Estimez les revenus locatifs pour ce bien</p>
                </div>
              </div>
              <button
                onClick={() => setIsEstimationModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content - Estimation Conciergerie uniquement (mode conciergerie_only) */}
            <div className="flex-1 overflow-y-auto">
              <EstimationPanelWithTabs
                mode="conciergerie_only"
                initialData={{
                  address: lead.propertyAddress ? `${lead.propertyAddress}, ${lead.propertyCity || ''} ${lead.propertyZipCode || ''}`.trim() : '',
                  propertyType: lead.propertyType === 'Maison' ? 'house' : 'apartment',
                  bedrooms: lead.propertyRooms || 1,
                  bathrooms: 1,
                  area: lead.propertySurface || 30,
                  maxGuests: (lead.propertyRooms || 1) * 2
                }}
                showInModal={true}
                onClose={() => setIsEstimationModalOpen(false)}
                onResult={async (result) => {
                  // Formater et ajouter les résultats dans les notes
                  const formatCurrency = (amount: number) => {
                    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
                  };

                  let noteContent = `📊 ESTIMATION REVENUS LOCATIFS\n\n`;
                  noteContent += `💰 Revenu Max Estimé: ${formatCurrency(result.highEstimate)}\n`;
                  noteContent += `📉 Revenu Min Estimé: ${formatCurrency(result.lowEstimate)}\n`;
                  noteContent += `📊 Revenu Moyen: ${formatCurrency((result.lowEstimate + result.highEstimate) / 2)}\n`;
                  noteContent += `📈 Taux d'occupation: ${result.occupancyRate}%\n`;
                  
                  if (result.averageNightlyPrice) {
                    noteContent += `🌙 Prix moyen / nuit: ${formatCurrency(result.averageNightlyPrice)}\n`;
                  }
                  
                  noteContent += `\n💼 NET PROPRIÉTAIRE (après 25% commission): ${formatCurrency(((result.lowEstimate + result.highEstimate) / 2) * 0.75)}\n`;
                  
                  if (result.marketAnalysis) {
                    noteContent += `\n💡 Analyse Marketing:\n${result.marketAnalysis}\n`;
                  }
                  
                  if (result.description) {
                    noteContent += `\n📝 Description:\n${result.description}\n`;
                  }

                  try {
                    const { error } = await storage.addMarketingLeadNote(lead.id, noteContent);
                    if (error) {
                      toast.error('Erreur lors de l\'ajout de la note: ' + (error.message || 'Erreur inconnue'));
                    } else {
                      toast.success('Estimation ajoutée aux notes');
                      onReload();
                      setIsEstimationModalOpen(false);
                    }
                  } catch (error: any) {
                    toast.error('Erreur: ' + (error.message || 'Erreur inconnue'));
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}


      {/* Modal de réaffectation */}
      {isReassignModalOpen && (
        <ReassignLeadModal
          lead={lead}
          profiles={profiles}
          userProfile={userProfile}
          onClose={() => setIsReassignModalOpen(false)}
          onReassign={async (newSetterUserId: string, reason: string) => {
            if (!userProfile?.id) {
              toast.error('Erreur: Utilisateur non identifié');
              return;
            }
            try {
              const { error } = await storage.reassignMarketingLead(
                lead.id,
                newSetterUserId,
                reason,
                userProfile.id
              );
              if (error) {
                toast.error('Erreur lors de la réaffectation: ' + (error.message || 'Erreur inconnue'));
              } else {
                toast.success('Lead réaffecté avec succès');
                setIsReassignModalOpen(false);
                // Recharger le lead depuis la base pour mettre à jour la modale
                const updatedLead = await storage.getMarketingLead(lead.id);
                if (updatedLead) {
                  setLead(updatedLead);
                  // Mettre à jour aussi dans le parent
                  onUpdate(lead.id, {
                    assignedToUserId: updatedLead.assignedToUserId,
                    assignedAt: updatedLead.assignedAt,
                    assignmentMethod: updatedLead.assignmentMethod,
                    assignmentReason: updatedLead.assignmentReason
                  });
                  // Recharger l'historique d'affectation
                  const assignments = await storage.getMarketingLeadAssignmentHistory(lead.id);
                  setLocalAssignmentHistory(assignments);
                }
                onReload();
              }
            } catch (error: any) {
              toast.error('Erreur: ' + (error.message || 'Erreur inconnue'));
            }
          }}
        />
      )}
    </div>
    </>
  );
};

// Modal de réaffectation
interface ReassignLeadModalProps {
  lead: MarketingLead;
  profiles: UserProfile[];
  userProfile: UserProfile | null;
  onClose: () => void;
  onReassign: (newSetterUserId: string, reason: string) => Promise<void>;
}

const ReassignLeadModal: React.FC<ReassignLeadModalProps> = ({
  lead,
  profiles,
  userProfile,
  onClose,
  onReassign
}) => {
  const { toast, prompt } = useModal();
  const [selectedSetterId, setSelectedSetterId] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [availableSetters, setAvailableSetters] = useState<UserProfile[]>([]);
  const [loadingSetters, setLoadingSetters] = useState(true);

  // Filtrer les setters du même provider (filtrage côté frontend)
  useEffect(() => {
    setLoadingSetters(true);
    
    console.log('Filtering setters:', {
      totalProfiles: profiles.length,
      userProfile: {
        id: userProfile?.id,
        role: userProfile?.role,
        marketingProviderId: userProfile?.marketingProviderId,
        isSupervisor: userProfile?.isSupervisor
      },
      allMarketingProfiles: profiles.filter(p => p.role === 'prestataire_marketing').map(p => ({
        id: p.id,
        name: `${p.firstName} ${p.lastName}`,
        email: p.email,
        providerId: p.marketingProviderId,
        isSupervisor: p.isSupervisor
      }))
    });
    
    // Filtrer les profils : même provider, exclure les superviseurs
    const filtered = profiles.filter(p => {
      // Doit être un prestataire marketing
      if (p.role !== 'prestataire_marketing') {
        return false;
      }
      // Exclure les superviseurs
      if (p.isSupervisor) {
        return false;
      }
      // Si l'utilisateur a un provider, filtrer sur le même provider
      if (userProfile?.marketingProviderId) {
        const sameProvider = p.marketingProviderId === userProfile.marketingProviderId;
        if (!sameProvider) {
          console.log(`Excluding ${p.firstName} ${p.lastName}: different provider (${p.marketingProviderId} vs ${userProfile.marketingProviderId})`);
        }
        return sameProvider;
      }
      // Sinon, inclure tous les setters
      return true;
    });
    
    console.log('Filtered setters:', {
      count: filtered.length,
      setters: filtered.map(p => ({
        id: p.id,
        name: `${p.firstName} ${p.lastName}`,
        email: p.email,
        providerId: p.marketingProviderId
      }))
    });
    
    setAvailableSetters(filtered);
    setLoadingSetters(false);
  }, [userProfile?.marketingProviderId, profiles]);

  const handleReassign = async () => {
    if (!selectedSetterId) {
      toast.error('Veuillez sélectionner un setter');
      return;
    }
    if (!reason.trim()) {
      const providedReason = await prompt({
        title: 'Raison de la réaffectation',
        message: 'Veuillez indiquer la raison de la réaffectation',
        placeholder: 'Ex: Répartition de charge, spécialisation...'
      });
      if (providedReason === null || !providedReason.trim()) {
        return;
      }
      setReason(providedReason);
      await onReassign(selectedSetterId, providedReason);
    } else {
      await onReassign(selectedSetterId, reason);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-amber-50 to-white border-b border-slate-200 p-6 flex items-center justify-between z-10 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <UserCheck className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">
                {lead.assignedToUserId ? 'Réaffecter le lead' : 'Affecter le lead'}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {lead.firstName} {lead.lastName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Sélection du setter */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Sélectionner un setter *
            </label>
            {loadingSetters ? (
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Chargement des setters...
              </div>
            ) : availableSetters.length === 0 ? (
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                Aucun setter disponible. Vérifiez que des utilisateurs avec le rôle "prestataire_marketing" existent dans votre provider.
              </div>
            ) : (
              <SearchableSelect
                options={availableSetters.map(setter => ({
                  value: setter.id,
                  label: setter.firstName && setter.lastName
                    ? `${setter.firstName} ${setter.lastName}`
                    : setter.email || 'Utilisateur inconnu'
                }))}
                value={selectedSetterId}
                onChange={setSelectedSetterId}
                placeholder="Choisir un setter..."
                icon={<UserCheck className="w-4 h-4" />}
                showAllOnOpen={true}
              />
            )}
          </div>

          {/* Raison (optionnelle, peut être demandée après) */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Raison de la réaffectation (optionnel)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Répartition de charge, spécialisation géographique..."
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all resize-none"
              rows={3}
            />
          </div>

          {/* Info actuelle si réaffectation */}
          {lead.assignedToUserId && (
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="text-sm text-slate-700">
                <div className="font-semibold mb-1">Affectation actuelle:</div>
                <div>
                  {(() => {
                    const currentSetter = profiles.find(p => p.id === lead.assignedToUserId);
                    return currentSetter
                      ? `${currentSetter.firstName || ''} ${currentSetter.lastName || ''}`.trim() || currentSetter.email
                      : 'Setter inconnu';
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-6 bg-slate-50">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2.5 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleReassign}
              className="px-6 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium shadow-sm transition-colors"
            >
              {lead.assignedToUserId ? 'Réaffecter' : 'Affecter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// Modal de création de lead manuel
interface CreateManualLeadModalProps {
  userProfile: UserProfile | null;
  onClose: () => void;
  onCreate: (lead: Partial<MarketingLead>) => void;
}

const CreateManualLeadModal: React.FC<CreateManualLeadModalProps> = ({ userProfile, onClose, onCreate }) => {
  const { toast } = useModal();
  const [formData, setFormData] = useState<Partial<MarketingLead>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    source: 'other',
    propertyAddress: '',
    propertyCity: '',
    propertyZipCode: '',
    propertyType: 'Appartement',
    propertySurface: undefined,
    propertyRooms: undefined,
    projectType: '',
    annonceUrl: '',
    providerId: userProfile?.marketingProviderId || undefined
  });
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [marketingProviders, setMarketingProviders] = useState<MarketingProvider[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  // Charger les providers si admin/superadmin
  useEffect(() => {
    const loadProviders = async () => {
      if (userProfile && (userProfile.role === 'admin' || userProfile.role === 'superadmin')) {
        setLoadingProviders(true);
        try {
          const providers = await storage.getMarketingProviders();
          setMarketingProviders(providers);
        } catch (error) {
          console.error('Error loading marketing providers', error);
        } finally {
          setLoadingProviders(false);
        }
      }
    };
    loadProviders();
  }, [userProfile]);

  const handleAddressSearch = async (query: string) => {
    setFormData({ ...formData, propertyAddress: query });
    if (query.length > 3) {
      try {
        const res = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5`);
        const data = await res.json();
        setAddressSuggestions(data.features || []);
      } catch (e) {
        console.error(e);
        setAddressSuggestions([]);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  const selectAddress = (feature: any) => {
    const address = feature.properties.name;
    const city = feature.properties.city;
    const zipCode = feature.properties.postcode;
    setFormData({
      ...formData,
      propertyAddress: address,
      propertyCity: city,
      propertyZipCode: zipCode
    });
    setAddressSuggestions([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Vérifier que le providerId est présent
    if (!formData.providerId) {
      toast.error('Veuillez sélectionner un prestataire marketing');
      return;
    }
    
    // Forcer "A définir" si firstName ou lastName est vide
    // Si projectType est vide, mettre null (pas de chaîne vide)
    const leadData = {
      ...formData,
      firstName: formData.firstName?.trim() || 'A définir',
      lastName: formData.lastName?.trim() || 'A définir',
      projectType: formData.projectType?.trim() || null
    };
    
    onCreate(leadData);
  };

  const isAdminOrSuperadmin = userProfile && (userProfile.role === 'admin' || userProfile.role === 'superadmin');
  const showProviderSelect = isAdminOrSuperadmin;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-amber-50 to-white border-b border-slate-200 p-6 flex items-center justify-between z-10 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Plus className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Créer un lead manuel</h2>
              <p className="text-sm text-slate-500 mt-0.5">Remplissez les informations du contact et du bien</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Section Contact */}
          <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserCheck className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Informations Contact</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Prénom</label>
                <div className="relative">
                  <UserCheck className="absolute left-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={formData.firstName || ''}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Jean"
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Nom</label>
                <input
                  type="text"
                  value={formData.lastName || ''}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Dupont"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Source *</label>
                <SearchableSelect
                  options={[
                    { value: 'meta', label: 'Meta', icon: React.createElement(Facebook, { className: 'w-4 h-4' }) },
                    { value: 'call', label: 'Appel', icon: React.createElement(Phone, { className: 'w-4 h-4' }) },
                    { value: 'estimation_form', label: 'Formulaire Estimation', icon: React.createElement(TrendingUp, { className: 'w-4 h-4' }) },
                    { value: 'leboncoin', label: 'Leboncoin', icon: React.createElement(Tag, { className: 'w-4 h-4' }) },
                    { value: 'pap', label: 'PAP', icon: React.createElement(Newspaper, { className: 'w-4 h-4' }) },
                    { value: 'seloger', label: 'SeLoger', icon: React.createElement(Building, { className: 'w-4 h-4' }) },
                    { value: 'other', label: 'Autre', icon: React.createElement(FileText, { className: 'w-4 h-4' }) }
                  ]}
                  value={formData.source || 'other'}
                  onChange={(value) => setFormData({ ...formData, source: value as MarketingLeadSource })}
                  placeholder="Sélectionner une source"
                  icon={<TrendingUp className="w-4 h-4" />}
                  showAllOnOpen={true}
                />
              </div>
              {showProviderSelect && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Prestataire Marketing *</label>
                  <SearchableSelect
                    options={marketingProviders.map(provider => ({
                      value: provider.id,
                      label: provider.name
                    }))}
                    value={formData.providerId || ''}
                    onChange={(value) => setFormData({ ...formData, providerId: value })}
                    placeholder={loadingProviders ? "Chargement..." : "Sélectionner un prestataire"}
                    icon={<Building className="w-4 h-4" />}
                    showAllOnOpen={true}
                    disabled={loadingProviders}
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="jean.dupont@example.com"
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Téléphone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="06 12 34 56 78"
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section Bien Immobilier */}
          <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-100 rounded-lg">
                <Building className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Bien Immobilier</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Adresse</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none z-10" />
                  <input
                    type="text"
                    value={formData.propertyAddress || ''}
                    onChange={(e) => handleAddressSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setAddressSuggestions([]);
                      }
                    }}
                    placeholder="Recherche auto..."
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                  />
                  {addressSuggestions.length > 0 && (
                    <ul className="absolute z-20 w-full bg-white border border-slate-200 rounded-xl mt-2 shadow-xl max-h-48 overflow-auto">
                      {addressSuggestions.map((s, i) => (
                        <li 
                          key={i} 
                          onClick={() => selectAddress(s)} 
                          className="p-3 hover:bg-slate-50 cursor-pointer text-sm border-b border-slate-50 last:border-0 text-slate-900"
                        >
                          <span className="font-bold block">{s.properties.name}</span>
                          <span className="text-slate-500 text-xs">{s.properties.postcode} {s.properties.city}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Ville</label>
                  <input
                    type="text"
                    value={formData.propertyCity || ''}
                    onChange={(e) => setFormData({ ...formData, propertyCity: e.target.value })}
                    placeholder="Paris"
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Code postal</label>
                  <input
                    type="text"
                    value={formData.propertyZipCode || ''}
                    onChange={(e) => setFormData({ ...formData, propertyZipCode: e.target.value })}
                    placeholder="75001"
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Type de bien</label>
                  <SearchableSelect
                    options={[
                      { value: 'Appartement', label: 'Appartement' },
                      { value: 'Maison', label: 'Maison' },
                      { value: 'Immeuble', label: 'Immeuble' },
                      { value: 'Autre', label: 'Autre' }
                    ]}
                    value={formData.propertyType || 'Appartement'}
                    onChange={(value) => setFormData({ ...formData, propertyType: value })}
                    placeholder="Type de bien"
                    icon={<Building className="w-4 h-4" />}
                    showAllOnOpen={true}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Surface (m²)</label>
                  <div className="relative">
                    <TrendingUp className="absolute left-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.propertySurface || ''}
                      onChange={(e) => setFormData({ ...formData, propertySurface: parseFloat(e.target.value) || undefined })}
                      placeholder="75"
                      className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Nombre de pièces</label>
                  <div className="relative">
                    <Building className="absolute left-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="number"
                      min="0"
                      value={formData.propertyRooms || ''}
                      onChange={(e) => setFormData({ ...formData, propertyRooms: parseInt(e.target.value) || undefined })}
                      placeholder="3"
                      className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Type de projet</label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'A définir' },
                    { value: 'Conciergerie', label: 'Conciergerie' },
                    { value: 'Sous-location', label: 'Sous-location' }
                  ]}
                  value={formData.projectType || ''}
                  onChange={(value) => setFormData({ ...formData, projectType: value || null })}
                  placeholder="Sélectionner un type de projet"
                  icon={<FileText className="w-4 h-4" />}
                  showAllOnOpen={true}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">URL de l'annonce</label>
                <div className="relative">
                  <ExternalLink className="absolute left-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="url"
                    value={formData.annonceUrl || ''}
                    onChange={(e) => setFormData({ ...formData, annonceUrl: e.target.value })}
                    placeholder="https://www.leboncoin.fr/..."
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium transition-colors shadow-sm"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium shadow-sm transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Créer le lead
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal de transfert
interface TransferLeadModalProps {
  lead: MarketingLead;
  agencies: Agency[];
  onClose: () => void;
  onTransfer: (leadId: string, agencyId: string, pipelineStage: DealStage, hasAppointment: boolean, appointmentDate?: string, appointmentNotes?: string) => void;
}

const TransferLeadModal: React.FC<TransferLeadModalProps> = ({ lead, agencies, onClose, onTransfer }) => {
  const [selectedAgencyId, setSelectedAgencyId] = useState<string>('');
  const [pipelineStage, setPipelineStage] = useState<DealStage>(DealStage.NEW);
  const [hasAppointment, setHasAppointment] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentNotes, setAppointmentNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAgencyId) return;
    
    let fullAppointmentDate: string | undefined;
    if (hasAppointment && appointmentDate && appointmentTime) {
      fullAppointmentDate = new Date(`${appointmentDate}T${appointmentTime}`).toISOString();
    }
    
    onTransfer(lead.id, selectedAgencyId, pipelineStage, hasAppointment, fullAppointmentDate, appointmentNotes);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-slate-900">Transférer le lead à une agence</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Agence *</label>
            <SearchableSelect
              options={agencies.map(agency => ({
                value: agency.id!,
                label: `${agency.name || agency.city} - ${agency.city}`,
                icon: <Building className="w-4 h-4" />
              }))}
              value={selectedAgencyId}
              onChange={(value) => setSelectedAgencyId(value)}
              placeholder="Sélectionner une agence..."
              icon={<Building className="w-4 h-4" />}
              searchPlaceholder="Rechercher une agence..."
              showAllOnOpen={true}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Pipeline *</label>
            <SearchableSelect
              options={[
                { value: DealStage.NEW, label: DealStage.NEW },
                { value: DealStage.QUALIFIED, label: DealStage.QUALIFIED },
                { value: DealStage.VISIT_SCHEDULED, label: DealStage.VISIT_SCHEDULED }
              ]}
              value={pipelineStage}
              onChange={(value) => setPipelineStage(value as DealStage)}
              placeholder="Sélectionner un stage..."
              icon={<TrendingUp className="w-4 h-4" />}
              showAllOnOpen={true}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="hasAppointment"
              checked={hasAppointment}
              onChange={(e) => setHasAppointment(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="hasAppointment" className="text-sm text-slate-700">Créer un rendez-vous</label>
          </div>
          {hasAppointment && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Date *</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="date"
                      required={hasAppointment}
                      value={appointmentDate}
                      onChange={(e) => setAppointmentDate(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Heure *</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="time"
                      required={hasAppointment}
                      value={appointmentTime}
                      onChange={(e) => setAppointmentTime(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 transition-all"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Notes</label>
                <textarea
                  value={appointmentNotes}
                  onChange={(e) => setAppointmentNotes(e.target.value)}
                  placeholder="Notes pour le rendez-vous..."
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 resize-none transition-all"
                  rows={3}
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium shadow-sm transition-colors flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Transférer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
